/**
 * Admin Shop Creator — pixel editor for cursor cosmetics.
 */
import {
  fetchAdminShopDrafts,
  fetchAdminShopDraft,
  saveAdminShopDraft,
  fetchAdminShopCrateDrafts,
  fetchAdminShopCrateDraft,
  saveAdminShopCrateDraft,
  publishAdminShopCosmetic,
  patchAdminShopCosmetic,
  patchAdminShopCrate,
  fetchAdminShopCosmetics,
  fetchAdminShopCrates,
  publishAdminShopCrate,
  searchUsersByName,
  type AdminShopDraftSummary,
  type AdminShopCosmeticRow,
  type AdminShopCrateRow,
  type CosmeticCategory,
  type ShopUnlockType,
  type TrailMode,
} from "../lib/cloud";
import { fetchAdminAchievements } from "./achievements-api";
import { drawReferenceCursor, drawReferenceCursorHitboxOutline, COSMETIC_ART_SIZE, COSMETIC_REF_CURSOR_R } from "./cursor-cosmetics";
import { shadeHexColor, encodeMatchShadeByte } from "./cosmetic-recolor";
import { paintPixel, MAX_ANIM_FRAMES, ANIM_FRAME_MS } from "./shop-creator-fx";
import {
  buildMultiColorTrailPreviewDataUrl,
  paintTrailCardPreview,
  createTrailPreviewState,
  TRAIL_PLACEHOLDER_IMAGE,
} from "./trail-cosmetics";
import { MAX_TRAIL_COLORS, BANNER_ART_W, BANNER_ART_H } from "../../lib/shopTypes";
import { parseLimitedDurationMs } from "../../lib/limitedDuration";
import { createAdminCampaignCosmetic } from "./campaign/api";
import { createAdminTaskCosmetic } from "./tasks/api";
import { createAdminMarathonCosmetic } from "./marathon/api";
import { createAdminBattlePassCosmetic, patchAdminBattlePassCosmetic } from "./battle-pass/api";
import { createModalCloseButton } from "./modal-close-button";
import { mountThemeCosmeticEditor } from "./theme-cosmetic-editor";
import type { ThemeCosmeticData } from "./theme-cosmetic";

const CANVAS_SIZE = COSMETIC_ART_SIZE;
const REF_CURSOR_R = COSMETIC_REF_CURSOR_R;

/** Max bytes for Shop/Crates Creator image Import (browser-side). */
const IMPORT_MAX_BYTES = 8 * 1024 * 1024;
const IMPORT_ACCEPT_MIME = new Set(["image/png", "image/jpeg"]);
const IMPORT_ACCEPT_EXT = new Set([".png", ".jpg", ".jpeg"]);
const IMPORT_RESOLUTION_CANDIDATES = [8, 16, 24, 32, 48, 64, 96, 128, 160, 192, 224, 256];

/** Same halo offsets as glow paint — stamp match-mask without brightening shade bytes. */
const MATCH_MASK_HALO: Array<[number, number]> = [
  [0, 0],
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [-1, 1],
  [1, -1],
  [-1, -1],
];

type Tool = "pencil" | "line" | "bucket" | "eraser" | "select" | "eyedropper" | "text";

type PaintLayer = {
  id: number;
  name: string;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  matchCanvas: HTMLCanvasElement;
  matchCtx: CanvasRenderingContext2D;
};

type LayerSnap = {
  outline: ImageData;
  activeLayerIndex: number;
  layers: Array<{
    id: number;
    name: string;
    main: ImageData;
    matchMask: ImageData;
  }>;
  animFrames: ImageData[];
  matchMaskAnimFrames: ImageData[];
};

type FloatingSelection = {
  x: number;
  y: number;
  w: number;
  h: number;
  pixels: ImageData;
  matchPixels: ImageData;
};

export type ShopCreatorEditItem = {
  id: string;
  name: string;
  imageData: string;
  outlineMaskData?: string | null;
  animationFrames?: string[] | null;
  category?: CosmeticCategory | null;
  trailMode?: TrailMode | null;
  trailColors?: string[] | null;
  themeData?: ThemeCosmeticData | null;
  allowCustomColor?: boolean;
  allowRepurchase?: boolean;
  matchCursorColor?: boolean;
  cursorColorShade?: number;
  matchCursorMaskData?: string | null;
  matchCursorMaskFrames?: string[] | null;
  unlockType: ShopUnlockType;
  priceDots?: number | null;
  unlockAchievementId?: string | null;
  unlockChallengeId?: string | null;
  unlockScoreMode?: string | null;
  unlockScoreValue?: number | null;
  limitedItem?: boolean;
  limitedDurationText?: string | null;
  limitedEndsAt?: string | null;
  newCursor?: boolean;
  sentOnly?: boolean;
};

export type AdminShopCreatorApi = {
  loadPublishedItem: (item: ShopCreatorEditItem) => void;
};

export type AdminCrateArtEditorApi = {
  loadCrate: (item: { id: string; name: string; imageData: string }) => void;
};

type CreatorMode =
  | "cosmetic"
  | "crateItem"
  | "crateArt"
  | "campaignItem"
  | "taskItem"
  | "marathonItem"
  | "battlePassItem";

type PixelCreatorOpts = {
  onCrateArtSaved?: () => void;
  /** Campaign exclusive publish: unlock node + callback after create. */
  unlockCampaignNodeId?: string | null;
  onCampaignItemPublished?: (cosmetic: { id: string; name: string; category: string }) => void;
  /** Task exclusive publish. */
  unlockTaskId?: string | null;
  onTaskItemPublished?: (cosmetic: { id: string; name: string; category: string }) => void;
  /** Marathon exclusive publish. */
  unlockMarathonSegmentId?: string | null;
  onMarathonItemPublished?: (cosmetic: { id: string; name: string; category: string }) => void;
  /** Battle Pass exclusive publish. */
  unlockBattlePassLevelId?: string | null;
  onBattlePassItemPublished?: (cosmetic: {
    id: string;
    name: string;
    category: string;
    imageData: string;
  }) => void;
  permissions?: {
    publish?: boolean;
    overwrite?: boolean;
  };
  /** Shop / Crates Creator Theme: open navigable Home preview. */
  onEnterThemeCosmeticPreview?: (api: {
    getData: () => ThemeCosmeticData | null;
    setData: (data: ThemeCosmeticData | null) => void;
    onExit: () => void;
    /** Admin nav button id to reopen after Back to Editor. */
    returnButtonId?: string;
  }) => void;
};

function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}

const SCORE_UNLOCK_MODES: Array<{ id: string; label: string }> = [
  { id: "normal", label: "Normal" },
  { id: "wasd", label: "WASD" },
  { id: "superdot", label: "Superdot" },
  { id: "progression", label: "Progression" },
  { id: "roulette", label: "Roulette" },
  { id: "sandbox", label: "Sandbox" },
  { id: "speedrun", label: "SpeedRun" },
  { id: "highway", label: "Highway" },
  { id: "darkness", label: "Darkness" },
  { id: "mirror", label: "Mirror" },
  { id: "snake", label: "Snake" },
  { id: "combo", label: "Combo" },
  { id: "overload", label: "Overload" },
  { id: "colorrush", label: "Color Rush" },
  { id: "heat", label: "Heat" },
  { id: "hunter", label: "Hunter" },
  { id: "pop", label: "POP" },
  { id: "counter", label: "Counter" },
  { id: "aim", label: "AIM" },
  { id: "hook", label: "Hook" },
  { id: "tilt", label: "Tilt" },
  { id: "orbit", label: "Orbit" },
  { id: "penalty", label: "Golf" },
];

function drawImageOntoCanvas(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  destW: number,
  destH: number,
  fit: "stretch" | "contain",
) {
  ctx.clearRect(0, 0, destW, destH);
  if (fit === "stretch") {
    ctx.drawImage(img, 0, 0, destW, destH);
    return;
  }
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (iw <= 1 && ih <= 1) return;
  const scale = Math.min(destW / iw, destH / ih);
  const w = iw * scale;
  const h = ih * scale;
  ctx.drawImage(img, (destW - w) / 2, (destH - h) / 2, w, h);
}

function importFileExtension(name: string): string {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

/** Validate PNG/JPG and size before decoding. Returns an error message or null. */
function validateImportImageFile(file: File): string | null {
  const ext = importFileExtension(file.name);
  const mimeOk = IMPORT_ACCEPT_MIME.has(file.type);
  const extOk = IMPORT_ACCEPT_EXT.has(ext);
  // Some browsers leave type empty — allow when the extension is clearly PNG/JPG.
  if (!mimeOk && !extOk) {
    return "Only PNG and JPG images are supported.";
  }
  if (file.type && !mimeOk) {
    return "Only PNG and JPG images are supported.";
  }
  if (file.size > IMPORT_MAX_BYTES) {
    const mb = Math.round(IMPORT_MAX_BYTES / (1024 * 1024));
    return `Image is too large (max ${mb} MB).`;
  }
  return null;
}

function importResolutionOptions(maxDim: number): number[] {
  const opts = IMPORT_RESOLUTION_CANDIDATES.filter((n) => n <= maxDim);
  return opts.length ? opts : [Math.max(1, maxDim)];
}

/** Nearest-neighbor downscale to an exact square grid (pixel-art style). */
function downscaleImageToGrid(img: HTMLImageElement, size: number): ImageData {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(img, 0, 0, size, size);
  return ctx.getImageData(0, 0, size, size);
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image."));
    };
    img.src = url;
  });
}

function mountAdminPixelCreator(
  root: HTMLElement,
  mode: CreatorMode,
  opts?: PixelCreatorOpts,
): AdminShopCreatorApi & AdminCrateArtEditorApi {
  root.replaceChildren();

  let editingItem: ShopCreatorEditItem | null = null;
  let editingCrateId: string | null = null;

  /** cursor = existing cosmetic editor; trail = trail cosmetics; banner = wide banner art. */
  let cosmeticKind: "cursor" | "trail" | "banner" | "theme" = "cursor";
  /** Current art buffer dimensions — 128x128 for cursor/trail, 256x64 for banner. */
  let artW = CANVAS_SIZE;
  let artH = CANVAS_SIZE;
  let trailMode: TrailMode = "multiColor";
  let trailColors: string[] = ["#ff3366", "#66ffaa"];
  let trailPreviewRaf = 0;
  let trailPreviewState = createTrailPreviewState();
  let trailPreviewT0 = performance.now();

  const toolbar = document.createElement("div");
  toolbar.className = "adminShopCreatorToolbar modeBox";

  const supportsTrail =
    mode === "cosmetic" ||
    mode === "crateItem" ||
    mode === "campaignItem" ||
    mode === "taskItem" ||
    mode === "marathonItem" ||
    mode === "battlePassItem";

  const typeGroup = document.createElement("div");
  typeGroup.className = "adminShopToolbarGroup";
  if (!supportsTrail) typeGroup.classList.add("hidden");
  const typeLabel = document.createElement("span");
  typeLabel.className = "adminShopToolbarLabel";
  typeLabel.textContent = "Cosmetic type";
  const typeSelect = document.createElement("select");
  typeSelect.className = "formInput adminShopToolSelect";
  typeSelect.setAttribute("aria-label", "Cosmetic type");
  (
    [
      ["cursor", "Cursor"],
      ["trail", "Trail"],
      ["banner", "Banner"],
      ["theme", "Theme"],
    ] as const
  ).forEach(([v, label]) => {
    const o = document.createElement("option");
    o.value = v;
    o.textContent = label;
    typeSelect.appendChild(o);
  });
  typeGroup.appendChild(typeLabel);
  typeGroup.appendChild(typeSelect);

  const trailModeGroup = document.createElement("div");
  trailModeGroup.className = "adminShopToolbarGroup hidden";
  const trailModeLabel = document.createElement("span");
  trailModeLabel.className = "adminShopToolbarLabel";
  trailModeLabel.textContent = "Trail Mode";
  const trailModeSelect = document.createElement("select");
  trailModeSelect.className = "formInput adminShopToolSelect";
  trailModeSelect.setAttribute("aria-label", "Trail Mode");
  (
    [
      ["multiColor", "Multi Color"],
      ["particle", "Particle"],
    ] as const
  ).forEach(([v, label]) => {
    const o = document.createElement("option");
    o.value = v;
    o.textContent = label;
    trailModeSelect.appendChild(o);
  });
  trailModeGroup.appendChild(trailModeLabel);
  trailModeGroup.appendChild(trailModeSelect);

  const toolsGroup = document.createElement("div");
  toolsGroup.className = "adminShopToolbarGroup";
  const toolsLabel = document.createElement("span");
  toolsLabel.className = "adminShopToolbarLabel";
  toolsLabel.textContent = "Tool";
  const toolSelect = document.createElement("select");
  toolSelect.className = "formInput adminShopToolSelect";
  const BASE_TOOLS: Tool[] = ["pencil", "line", "bucket", "eraser", "eyedropper", "select"];
  function toolOptionLabel(t: Tool): string {
    return t === "eyedropper" ? "Eyedropper" : t.charAt(0).toUpperCase() + t.slice(1);
  }
  function syncToolSelectOptions() {
    const wanted = cosmeticKind === "banner" ? [...BASE_TOOLS, "text" as Tool] : BASE_TOOLS;
    const cur = toolSelect.value;
    toolSelect.replaceChildren();
    wanted.forEach((t) => {
      const o = document.createElement("option");
      o.value = t;
      o.textContent = toolOptionLabel(t);
      toolSelect.appendChild(o);
    });
    if (wanted.includes(cur as Tool)) {
      toolSelect.value = cur;
    } else {
      tool = "pencil";
      toolSelect.value = "pencil";
    }
  }
  BASE_TOOLS.forEach((t) => {
    const o = document.createElement("option");
    o.value = t;
    o.textContent = toolOptionLabel(t);
    toolSelect.appendChild(o);
  });
  toolsGroup.appendChild(toolsLabel);
  toolsGroup.appendChild(toolSelect);

  const importBtn = document.createElement("button");
  importBtn.type = "button";
  importBtn.className = "btn adminShopImportBtn";
  importBtn.textContent = "Import";
  importBtn.title = "Import a PNG or JPG as a movable selection on the canvas";
  toolsGroup.appendChild(importBtn);

  const importFileInput = document.createElement("input");
  importFileInput.type = "file";
  importFileInput.accept = "image/png,image/jpeg,.png,.jpg,.jpeg";
  importFileInput.className = "hidden";
  importFileInput.setAttribute("aria-hidden", "true");
  toolsGroup.appendChild(importFileInput);

  const colorGroup = document.createElement("div");
  colorGroup.className = "adminShopToolbarGroup";
  const colorLabel = document.createElement("span");
  colorLabel.className = "adminShopToolbarLabel";
  colorLabel.textContent = "Color";
  const colorInput = document.createElement("input");
  colorInput.type = "color";
  colorInput.className = "adminShopColorInput";
  colorInput.value = "#ff66ff";
  colorInput.setAttribute("aria-label", "Draw color");
  colorGroup.appendChild(colorLabel);
  colorGroup.appendChild(colorInput);

  const sizeGroup = document.createElement("div");
  sizeGroup.className = "adminShopToolbarGroup";
  const sizeLabel = document.createElement("span");
  sizeLabel.className = "adminShopToolbarLabel";
  sizeLabel.textContent = "Size";
  const sizeDisplay = document.createElement("span");
  sizeDisplay.className = "adminShopToolbarLabel adminShopSizeDisplay";
  sizeDisplay.textContent = "1px";
  const sizeSlider = document.createElement("input");
  sizeSlider.type = "range";
  sizeSlider.min = "1";
  sizeSlider.max = "8";
  sizeSlider.value = "1";
  sizeSlider.className = "adminShopSizeSlider";
  sizeSlider.setAttribute("aria-label", "Pencil size");
  sizeSlider.addEventListener("input", () => {
    pencilSize = parseInt(sizeSlider.value, 10);
    sizeDisplay.textContent = `${pencilSize}px`;
  });
  sizeGroup.appendChild(sizeLabel);
  sizeGroup.appendChild(sizeSlider);
  sizeGroup.appendChild(sizeDisplay);

  const fxGroup = document.createElement("div");
  fxGroup.className = "adminShopToolbarGroup";
  const fxLabel = document.createElement("span");
  fxLabel.className = "adminShopToolbarLabel";
  fxLabel.textContent = "FX";
  const glowCheck = document.createElement("input");
  glowCheck.type = "checkbox";
  glowCheck.id = "adminShopGlow";
  const glowLabel = document.createElement("label");
  glowLabel.htmlFor = glowCheck.id;
  glowLabel.className = "adminShopCheckLabel";
  glowLabel.appendChild(glowCheck);
  glowLabel.appendChild(document.createTextNode(" Glow"));
  const outlineCheck = document.createElement("input");
  outlineCheck.type = "checkbox";
  outlineCheck.id = "adminShopOutline";
  const outlineLabel = document.createElement("label");
  outlineLabel.htmlFor = outlineCheck.id;
  outlineLabel.className = "adminShopCheckLabel";
  outlineLabel.title =
    "Paint on the Outline layer (not tinted in-game). Works with Pencil, Line, Bucket, and Eraser.";
  outlineLabel.appendChild(outlineCheck);
  outlineLabel.appendChild(document.createTextNode(" Outline"));
  const animCheck = document.createElement("input");
  animCheck.type = "checkbox";
  animCheck.id = "adminShopAnim";
  const animLabel = document.createElement("label");
  animLabel.htmlFor = animCheck.id;
  animLabel.className = "adminShopCheckLabel";
  animLabel.appendChild(animCheck);
  animLabel.appendChild(document.createTextNode(" Animation"));

  const animControlsWrap = document.createElement("div");
  animControlsWrap.className = "adminShopAnimControls hidden";
  animControlsWrap.setAttribute("aria-hidden", "true");

  const frameCountInput = document.createElement("input");
  frameCountInput.type = "number";
  frameCountInput.min = "1";
  frameCountInput.max = String(MAX_ANIM_FRAMES);
  frameCountInput.value = "4";
  frameCountInput.className = "formInput adminShopFrameCountInput";
  frameCountInput.title = "Animation frame count";
  frameCountInput.setAttribute("aria-label", "Animation frame count");
  const frameIndexDisplay = document.createElement("span");
  frameIndexDisplay.className = "adminShopFrameIndex";
  frameIndexDisplay.textContent = "1/4";
  const prevFrameBtn = document.createElement("button");
  prevFrameBtn.type = "button";
  prevFrameBtn.className = "btn adminShopFrameNavBtn";
  prevFrameBtn.textContent = "◀";
  prevFrameBtn.title = "Previous frame";
  const nextFrameBtn = document.createElement("button");
  nextFrameBtn.type = "button";
  nextFrameBtn.className = "btn adminShopFrameNavBtn";
  nextFrameBtn.textContent = "▶";
  nextFrameBtn.title = "Next frame";
  const playCheck = document.createElement("input");
  playCheck.type = "checkbox";
  playCheck.id = "adminShopAnimPlay";
  const playLabel = document.createElement("label");
  playLabel.htmlFor = playCheck.id;
  playLabel.className = "adminShopCheckLabel";
  playLabel.appendChild(playCheck);
  playLabel.appendChild(document.createTextNode(" Play"));
  const duplicateFrameBtn = document.createElement("button");
  duplicateFrameBtn.type = "button";
  duplicateFrameBtn.className = "btn adminShopDuplicateFrameBtn";
  duplicateFrameBtn.textContent = "Duplicate This Frame";
  duplicateFrameBtn.title = "Copy the current frame onto another frame slot";
  const duplicateFramePicker = document.createElement("div");
  duplicateFramePicker.className = "adminShopDuplicateFramePicker hidden";
  duplicateFramePicker.setAttribute("role", "group");
  duplicateFramePicker.setAttribute("aria-label", "Paste current frame onto…");

  animControlsWrap.appendChild(frameCountInput);
  animControlsWrap.appendChild(prevFrameBtn);
  animControlsWrap.appendChild(frameIndexDisplay);
  animControlsWrap.appendChild(nextFrameBtn);
  animControlsWrap.appendChild(playLabel);
  animControlsWrap.appendChild(duplicateFrameBtn);
  animControlsWrap.appendChild(duplicateFramePicker);

  fxGroup.appendChild(fxLabel);
  fxGroup.appendChild(glowLabel);
  fxGroup.appendChild(outlineLabel);
  fxGroup.appendChild(animLabel);
  fxGroup.appendChild(animControlsWrap);

  const createOutlineBtn = document.createElement("button");
  createOutlineBtn.type = "button";
  createOutlineBtn.className = "btn adminShopCreateOutlineBtn";
  createOutlineBtn.textContent = "Create Outline";
  createOutlineBtn.title =
    "Stamp a perfect outline of the cursor guide onto the Outline layer (for single-color fills).";
  fxGroup.appendChild(createOutlineBtn);

  const matchCursorGroup = document.createElement("div");
  matchCursorGroup.className = "adminShopToolbarGroup";
  const matchCursorTitle = document.createElement("span");
  matchCursorTitle.className = "adminShopToolbarLabel";
  matchCursorTitle.textContent = "Cursor fill";
  const matchCursorCheck = document.createElement("input");
  matchCursorCheck.type = "checkbox";
  matchCursorCheck.id = "adminShopMatchCursor";
  const matchCursorLabel = document.createElement("label");
  matchCursorLabel.htmlFor = matchCursorCheck.id;
  matchCursorLabel.className = "adminShopCheckLabel";
  matchCursorLabel.title =
    "Paint with the guide cursor color. Existing pixels stay; only new strokes use the shade.";
  matchCursorLabel.appendChild(matchCursorCheck);
  matchCursorLabel.appendChild(document.createTextNode(" Match cursor"));
  const matchShadeWrap = document.createElement("div");
  matchShadeWrap.className = "adminShopShadeRow adminShopShadeRow--toolbar hidden";
  const matchShadeInput = document.createElement("input");
  matchShadeInput.type = "range";
  matchShadeInput.min = "-100";
  matchShadeInput.max = "100";
  matchShadeInput.step = "1";
  matchShadeInput.value = "0";
  matchShadeInput.className = "adminShopShadeSlider";
  matchShadeInput.title = "Darker ← → Lighter (affects new strokes only)";
  matchShadeInput.setAttribute("aria-label", "Cursor color shade");
  const matchShadeHint = document.createElement("span");
  matchShadeHint.className = "accessHint adminShopShadeHint";
  matchShadeHint.textContent = "Exact";
  matchShadeWrap.appendChild(matchShadeInput);
  matchShadeWrap.appendChild(matchShadeHint);
  matchCursorGroup.appendChild(matchCursorTitle);
  matchCursorGroup.appendChild(matchCursorLabel);
  matchCursorGroup.appendChild(matchShadeWrap);

  const zoomGroup = document.createElement("div");
  zoomGroup.className = "adminShopToolbarGroup";
  const zoomLabel = document.createElement("span");
  zoomLabel.className = "adminShopToolbarLabel";
  zoomLabel.textContent = "Size";
  const zoomSelect = document.createElement("select");
  zoomSelect.className = "formInput adminShopZoomSelect";
  [1, 2, 3, 4, 6, 8, 10, 12].forEach((z) => {
    const o = document.createElement("option");
    o.value = String(z);
    o.textContent = `${z}x`;
    zoomSelect.appendChild(o);
  });
  zoomSelect.value = "8";
  zoomGroup.appendChild(zoomLabel);
  zoomGroup.appendChild(zoomSelect);

  const refToggle = document.createElement("label");
  refToggle.className = "adminShopRefToggle";
  const refCheck = document.createElement("input");
  refCheck.type = "checkbox";
  refCheck.checked = true;
  refToggle.appendChild(refCheck);
  refToggle.append(" Show cursor guide");

  const newCursorToggle = document.createElement("label");
  newCursorToggle.className = "adminShopRefToggle";
  newCursorToggle.title =
    "Hide the filled play cursor in-game; only your art is visible. Hitbox stays the same. Locks publish category to Cursor.";
  const newCursorCheck = document.createElement("input");
  newCursorCheck.type = "checkbox";
  newCursorCheck.id = "adminShopNewCursor";
  newCursorToggle.appendChild(newCursorCheck);
  newCursorToggle.append(" New Cursor");
  if (mode === "crateArt") newCursorToggle.classList.add("hidden");

  const symmetryToggle = document.createElement("label");
  symmetryToggle.className = "adminShopRefToggle";
  symmetryToggle.title =
    "Show a cross through the cursor guide center for symmetrical drawing (editor only).";
  const symmetryCheck = document.createElement("input");
  symmetryCheck.type = "checkbox";
  symmetryCheck.id = "adminShopSymmetryCross";
  symmetryToggle.appendChild(symmetryCheck);
  symmetryToggle.append(" Symmetry cross");

  const guideGroup = document.createElement("div");
  guideGroup.className = "adminShopToolbarGroup";
  const guideLabel = document.createElement("span");
  guideLabel.className = "adminShopToolbarLabel";
  guideLabel.textContent = "Guide";
  const guideColors = document.createElement("div");
  guideColors.className = "adminShopGuideColors";
  const cursorGuideColor = document.createElement("input");
  cursorGuideColor.type = "color";
  cursorGuideColor.className = "adminShopColorInput";
  cursorGuideColor.value = "#ffffff";
  cursorGuideColor.title = "Cursor guide color";
  cursorGuideColor.setAttribute("aria-label", "Cursor guide color");
  const canvasBgColor = document.createElement("input");
  canvasBgColor.type = "color";
  canvasBgColor.className = "adminShopColorInput";
  canvasBgColor.value = "#1a1a22";
  canvasBgColor.title = "Canvas background";
  canvasBgColor.setAttribute("aria-label", "Canvas background color");
  guideColors.appendChild(cursorGuideColor);
  guideColors.appendChild(canvasBgColor);
  guideGroup.appendChild(guideLabel);
  guideGroup.appendChild(guideColors);
  guideGroup.appendChild(refToggle);
  guideGroup.appendChild(newCursorToggle);
  guideGroup.appendChild(symmetryToggle);

  const undoBtn = document.createElement("button");
  undoBtn.type = "button";
  undoBtn.className = "btn";
  undoBtn.textContent = "Undo";
  undoBtn.disabled = true;

  const redoBtn = document.createElement("button");
  redoBtn.type = "button";
  redoBtn.className = "btn";
  redoBtn.textContent = "Redo";
  redoBtn.disabled = true;

  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className = "btn";
  resetBtn.textContent = "Reset";
  resetBtn.title = "Clear canvas and reset editor";

  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "btn green";
  saveBtn.textContent = "Save project";

  const loadBtn = document.createElement("button");
  loadBtn.type = "button";
  loadBtn.className = "btn";
  loadBtn.textContent = "Load project";

  const publishBtn = document.createElement("button");
  publishBtn.type = "button";
  publishBtn.className = "btn green";
  publishBtn.textContent =
    mode === "crateArt"
      ? "Save art"
      : mode === "crateItem"
        ? "Publish item"
        : mode === "battlePassItem"
          ? "Done"
          : mode === "campaignItem" || mode === "taskItem" || mode === "marathonItem"
            ? "Publish"
            : "Publish to Shop";

  const historyGroup = document.createElement("div");
  historyGroup.className = "adminShopToolbarGroup adminShopToolbarGroup--actions";
  historyGroup.appendChild(undoBtn);
  historyGroup.appendChild(redoBtn);
  historyGroup.appendChild(resetBtn);

  const fileGroup = document.createElement("div");
  fileGroup.className = "adminShopToolbarGroup adminShopToolbarGroup--actions";
  fileGroup.appendChild(saveBtn);
  fileGroup.appendChild(loadBtn);
  fileGroup.appendChild(publishBtn);

  toolbar.appendChild(typeGroup);
  toolbar.appendChild(trailModeGroup);
  toolbar.appendChild(toolsGroup);
  toolbar.appendChild(colorGroup);
  toolbar.appendChild(sizeGroup);
  toolbar.appendChild(fxGroup);
  toolbar.appendChild(matchCursorGroup);
  toolbar.appendChild(zoomGroup);
  toolbar.appendChild(guideGroup);
  toolbar.appendChild(historyGroup);
  toolbar.appendChild(fileGroup);
  root.appendChild(toolbar);

  const trailColorsPanel = document.createElement("div");
  trailColorsPanel.className = "adminShopTrailColors modeBox hidden";
  const trailColorsTitle = document.createElement("span");
  trailColorsTitle.className = "adminShopToolbarLabel";
  trailColorsTitle.textContent = "Trail colors (1–10, order = near cursor → oldest)";
  const trailColorsList = document.createElement("div");
  trailColorsList.className = "adminShopTrailColorsList";
  const trailGradientPreview = document.createElement("div");
  trailGradientPreview.className = "adminShopTrailGradientPreview";
  const trailColorActions = document.createElement("div");
  trailColorActions.className = "adminShopTrailColorActions";
  const addTrailColorBtn = document.createElement("button");
  addTrailColorBtn.type = "button";
  addTrailColorBtn.className = "btn";
  addTrailColorBtn.textContent = "Add color";
  const removeTrailColorBtn = document.createElement("button");
  removeTrailColorBtn.type = "button";
  removeTrailColorBtn.className = "btn";
  removeTrailColorBtn.textContent = "Remove color";
  trailColorActions.appendChild(addTrailColorBtn);
  trailColorActions.appendChild(removeTrailColorBtn);
  trailColorsPanel.appendChild(trailColorsTitle);
  trailColorsPanel.appendChild(trailColorsList);
  trailColorsPanel.appendChild(trailGradientPreview);
  trailColorsPanel.appendChild(trailColorActions);
  if (supportsTrail) root.appendChild(trailColorsPanel);

  const themeEditor = supportsTrail
    ? mountThemeCosmeticEditor(root, {
        onEnterNavigablePreview: opts?.onEnterThemeCosmeticPreview
          ? (api) =>
              opts.onEnterThemeCosmeticPreview!({
                ...api,
                returnButtonId:
                  mode === "crateItem" ? "adminBtnCrateCreator" : "adminBtnShopCreator",
              })
          : undefined,
      })
    : null;

  const workspace = document.createElement("div");
  workspace.className = "adminShopCreatorWorkspace";

  const viewport = document.createElement("div");
  viewport.className = "adminShopCreatorViewport";
  const canvas = document.createElement("canvas");
  canvas.className = "adminShopCreatorCanvas";
  canvas.width = artW;
  canvas.height = artH;
  viewport.appendChild(canvas);

  const selectActions = document.createElement("div");
  selectActions.className = "adminShopSelectActions hidden";
  const duplicateSelectionBtn = document.createElement("button");
  duplicateSelectionBtn.type = "button";
  duplicateSelectionBtn.className = "btn";
  duplicateSelectionBtn.textContent = "Duplicate";
  duplicateSelectionBtn.title = "Duplicate the current selection";
  selectActions.appendChild(duplicateSelectionBtn);
  viewport.appendChild(selectActions);

  const layersPanel = document.createElement("div");
  layersPanel.className = "adminShopLayersPanel modeBox";
  const layersTitle = document.createElement("span");
  layersTitle.className = "adminShopToolbarLabel";
  layersTitle.textContent = "Layers";
  const layersList = document.createElement("div");
  layersList.className = "adminShopLayersList";
  const layersActions = document.createElement("div");
  layersActions.className = "adminShopLayersActions";
  const addLayerBtn = document.createElement("button");
  addLayerBtn.type = "button";
  addLayerBtn.className = "btn";
  addLayerBtn.textContent = "Add layer";
  const deleteLayerBtn = document.createElement("button");
  deleteLayerBtn.type = "button";
  deleteLayerBtn.className = "btn";
  deleteLayerBtn.textContent = "Delete layer";
  layersActions.appendChild(addLayerBtn);
  layersActions.appendChild(deleteLayerBtn);
  layersPanel.appendChild(layersTitle);
  layersPanel.appendChild(layersList);
  layersPanel.appendChild(layersActions);

  workspace.appendChild(viewport);
  workspace.appendChild(layersPanel);
  root.appendChild(workspace);

  const trailPreviewWrap = document.createElement("div");
  trailPreviewWrap.className = "adminShopTrailPreviewWrap hidden";
  const trailPreviewLabel = document.createElement("span");
  trailPreviewLabel.className = "adminShopToolbarLabel";
  trailPreviewLabel.textContent = "Trail preview";
  const trailPreviewCanvas = document.createElement("canvas");
  trailPreviewCanvas.className = "adminShopTrailPreview";
  trailPreviewCanvas.width = 220;
  trailPreviewCanvas.height = 140;
  trailPreviewWrap.appendChild(trailPreviewLabel);
  trailPreviewWrap.appendChild(trailPreviewCanvas);
  if (supportsTrail) root.appendChild(trailPreviewWrap);

  const status = document.createElement("p");
  status.className = "accessHint";
  root.appendChild(status);

  let nextPaintLayerId = 1;
  let paintLayers: PaintLayer[] = [];
  let activeLayerIndex = 0;

  function makePaintLayer(name?: string): PaintLayer {
    const canvasEl = document.createElement("canvas");
    canvasEl.width = artW;
    canvasEl.height = artH;
    const ctx = canvasEl.getContext("2d")!;
    ctx.clearRect(0, 0, artW, artH);
    const matchCanvas = document.createElement("canvas");
    matchCanvas.width = artW;
    matchCanvas.height = artH;
    const matchCtx = matchCanvas.getContext("2d")!;
    matchCtx.clearRect(0, 0, artW, artH);
    const id = nextPaintLayerId++;
    return {
      id,
      name: name || `Layer ${id}`,
      canvas: canvasEl,
      ctx,
      matchCanvas,
      matchCtx,
    };
  }

  function resetPaintLayersToOne(seed?: PaintLayer) {
    paintLayers = [seed || makePaintLayer("Layer 1")];
    activeLayerIndex = 0;
    nextPaintLayerId = Math.max(2, paintLayers[0].id + 1);
  }

  resetPaintLayersToOne();

  const outlineOff = document.createElement("canvas");
  outlineOff.width = artW;
  outlineOff.height = artH;
  const outlineCtx = outlineOff.getContext("2d")!;
  outlineCtx.clearRect(0, 0, artW, artH);

  const animOffs: HTMLCanvasElement[] = [];
  const animCtxs: CanvasRenderingContext2D[] = [];
  const matchMaskAnimOffs: HTMLCanvasElement[] = [];
  const matchMaskAnimCtxs: CanvasRenderingContext2D[] = [];
  let frameCount = 4;
  let currentFrameIndex = 0;
  let playFrameIndex = 0;
  let glowEnabled = false;
  let pencilSize = 1;
  let animEnabled = false;
  let playEnabled = false;
  let playRaf = 0;
  let playLast = 0;

  function activePaintLayerObj(): PaintLayer {
    return paintLayers[clamp(activeLayerIndex, 0, paintLayers.length - 1)];
  }

  function compositePaintLayersOnto(targetCtx: CanvasRenderingContext2D) {
    for (const layer of paintLayers) {
      targetCtx.drawImage(layer.canvas, 0, 0);
    }
  }

  function compositeMatchMasksOnto(targetCtx: CanvasRenderingContext2D) {
    for (const layer of paintLayers) {
      targetCtx.drawImage(layer.matchCanvas, 0, 0);
    }
  }

  function getCompositeMainImageData(): ImageData {
    const c = document.createElement("canvas");
    c.width = artW;
    c.height = artH;
    const ctx = c.getContext("2d")!;
    ctx.clearRect(0, 0, artW, artH);
    compositePaintLayersOnto(ctx);
    return ctx.getImageData(0, 0, artW, artH);
  }

  function syncLayersUi() {
    layersList.replaceChildren();
    paintLayers.forEach((layer, idx) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn adminShopLayerBtn" + (idx === activeLayerIndex ? " green" : "");
      btn.textContent = layer.name;
      btn.title = `Select ${layer.name}`;
      btn.onclick = () => {
        if (floating) commitFloating();
        activeLayerIndex = idx;
        syncLayersUi();
        syncSelectActionsUi();
        status.textContent = `Active: ${layer.name}. Select / paint only affects this layer.`;
        redrawEditor();
      };
      layersList.appendChild(btn);
    });
    deleteLayerBtn.disabled = paintLayers.length <= 1;
  }

  function syncSelectActionsUi() {
    const show = tool === "select" && !!floating;
    selectActions.classList.toggle("hidden", !show);
  }

  function matchCursorShade(): number {
    return Math.max(-1, Math.min(1, (Number(matchShadeInput.value) || 0) / 100));
  }

  function syncMatchShadeHint() {
    const v = Number(matchShadeInput.value) || 0;
    if (v < -5) matchShadeHint.textContent = "Darker";
    else if (v > 5) matchShadeHint.textContent = "Lighter";
    else matchShadeHint.textContent = "Exact";
  }

  /** Brush color for new strokes. Match-cursor mode uses shaded guide color; does not rewrite existing pixels. */
  function currentDrawColor(): string {
    if (matchCursorCheck.checked) {
      return shadeHexColor(cursorGuideColor.value || "#ffffff", matchCursorShade());
    }
    return colorInput.value;
  }

  function syncMatchCursorUi() {
    const on = matchCursorCheck.checked;
    matchShadeWrap.classList.toggle("hidden", !on);
    colorInput.disabled = on;
    colorGroup.classList.toggle("adminShopToolbarGroup--dimmed", on);
    if (on) {
      colorInput.value = shadeHexColor(cursorGuideColor.value || "#ffffff", matchCursorShade());
    }
    syncMatchShadeHint();
  }

  function ensureAnimFrameCanvases(count: number) {
    while (animOffs.length < count) {
      const c = document.createElement("canvas");
      c.width = artW;
      c.height = artH;
      animOffs.push(c);
      const ctx = c.getContext("2d")!;
      ctx.clearRect(0, 0, artW, artH);
      animCtxs.push(ctx);

      const mc = document.createElement("canvas");
      mc.width = artW;
      mc.height = artH;
      matchMaskAnimOffs.push(mc);
      const mctx = mc.getContext("2d")!;
      mctx.clearRect(0, 0, artW, artH);
      matchMaskAnimCtxs.push(mctx);
    }
    while (animOffs.length > count) {
      animOffs.pop();
      animCtxs.pop();
      matchMaskAnimOffs.pop();
      matchMaskAnimCtxs.pop();
    }
  }

  function clearAllAnimFrames() {
    for (const ctx of animCtxs) {
      ctx.clearRect(0, 0, artW, artH);
    }
    for (const ctx of matchMaskAnimCtxs) {
      ctx.clearRect(0, 0, artW, artH);
    }
  }

  function getMainPaintCtx(): CanvasRenderingContext2D {
    if (animEnabled) {
      ensureAnimFrameCanvases(frameCount);
      return animCtxs[currentFrameIndex];
    }
    return activePaintLayerObj().ctx;
  }

  function getMatchMaskPaintCtx(): CanvasRenderingContext2D {
    if (animEnabled) {
      ensureAnimFrameCanvases(frameCount);
      return matchMaskAnimCtxs[currentFrameIndex];
    }
    return activePaintLayerObj().matchCtx;
  }

  function stampMatchMaskAt(x: number, y: number, shade: number | null, glow: boolean) {
    const ctx = getMatchMaskPaintCtx();
    const cells = glow && shade != null ? MATCH_MASK_HALO : ([[0, 0]] as Array<[number, number]>);
    if (shade == null) {
      for (const [dx, dy] of cells) {
        const px = x + dx;
        const py = y + dy;
        if (px < 0 || py < 0 || px >= artW || py >= artH) continue;
        ctx.clearRect(px, py, 1, 1);
      }
      return;
    }
    const v = encodeMatchShadeByte(shade);
    ctx.fillStyle = `rgb(${v},${v},${v})`;
    for (const [dx, dy] of cells) {
      const px = x + dx;
      const py = y + dy;
      if (px < 0 || py < 0 || px >= artW || py >= artH) continue;
      ctx.fillRect(px, py, 1, 1);
    }
  }

  /** Resize art buffers for a cosmetic-type switch (cursor/trail = 128x128, banner = 256x64). No-op if unchanged. */
  function resizeArtBuffers(w: number, h: number) {
    if (w === artW && h === artH) return;
    artW = w;
    artH = h;
    floating = null;
    marqueeStart = null;
    marqueeEnd = null;
    selectMode = null;
    moveGrab = null;
    lineStart = null;
    snapshot = null;
    drawing = false;
    hideDuplicateFramePicker();

    canvas.width = artW;
    canvas.height = artH;

    outlineOff.width = artW;
    outlineOff.height = artH;
    outlineCtx.clearRect(0, 0, artW, artH);

    for (const layer of paintLayers) {
      layer.canvas.width = artW;
      layer.canvas.height = artH;
      layer.ctx.clearRect(0, 0, artW, artH);
      layer.matchCanvas.width = artW;
      layer.matchCanvas.height = artH;
      layer.matchCtx.clearRect(0, 0, artW, artH);
    }

    animOffs.forEach((c, i) => {
      c.width = artW;
      c.height = artH;
      animCtxs[i].clearRect(0, 0, artW, artH);
    });
    matchMaskAnimOffs.forEach((c, i) => {
      c.width = artW;
      c.height = artH;
      matchMaskAnimCtxs[i].clearRect(0, 0, artW, artH);
    });

    history.length = 0;
    historyIndex = -1;
    pushHistory();
  }

  function editorDisplayFrameIndex() {
    return playEnabled && animEnabled ? playFrameIndex : currentFrameIndex;
  }

  function updateFrameDisplay() {
    frameIndexDisplay.textContent = `${currentFrameIndex + 1}/${frameCount}`;
  }

  function hideDuplicateFramePicker() {
    duplicateFramePicker.classList.add("hidden");
    duplicateFramePicker.replaceChildren();
    duplicateFrameBtn.setAttribute("aria-expanded", "false");
  }

  function syncAnimControls() {
    const on = animEnabled;
    animControlsWrap.classList.toggle("hidden", !on);
    animControlsWrap.setAttribute("aria-hidden", on ? "false" : "true");
    if (!on && playEnabled) {
      playEnabled = false;
      playCheck.checked = false;
      stopPlayLoop();
    }
    if (!on) hideDuplicateFramePicker();
    updateFrameDisplay();
  }

  function duplicateCurrentFrameOnto(targetIndex: number) {
    if (!animEnabled) return;
    ensureAnimFrameCanvases(frameCount);
    const src = currentFrameIndex;
    const dst = clamp(Math.floor(targetIndex), 0, frameCount - 1);
    if (dst === src) {
      hideDuplicateFramePicker();
      status.textContent = `Frame ${src + 1} is already the current frame.`;
      return;
    }
    if (floating) commitFloating();
    const paint = animCtxs[src].getImageData(0, 0, artW, artH);
    const match = matchMaskAnimCtxs[src].getImageData(0, 0, artW, artH);
    animCtxs[dst].putImageData(paint, 0, 0);
    matchMaskAnimCtxs[dst].putImageData(match, 0, 0);
    pushHistory();
    hideDuplicateFramePicker();
    status.textContent = `Duplicated frame ${src + 1} onto frame ${dst + 1}.`;
    redrawEditor();
  }

  function openDuplicateFramePicker() {
    if (!animEnabled) return;
    if (!duplicateFramePicker.classList.contains("hidden")) {
      hideDuplicateFramePicker();
      return;
    }
    duplicateFramePicker.replaceChildren();
    const hint = document.createElement("span");
    hint.className = "accessHint adminShopDuplicateFrameHint";
    hint.textContent = `Paste frame ${currentFrameIndex + 1} onto:`;
    duplicateFramePicker.appendChild(hint);
    for (let i = 0; i < frameCount; i++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn adminShopFrameNavBtn";
      btn.textContent = String(i + 1);
      btn.title =
        i === currentFrameIndex
          ? `Frame ${i + 1} (current)`
          : `Overwrite frame ${i + 1} with frame ${currentFrameIndex + 1}`;
      if (i === currentFrameIndex) btn.classList.add("adminShopFrameNavBtn--current");
      btn.onclick = () => duplicateCurrentFrameOnto(i);
      duplicateFramePicker.appendChild(btn);
    }
    duplicateFramePicker.classList.remove("hidden");
    duplicateFrameBtn.setAttribute("aria-expanded", "true");
  }

  function refreshFxStatusHint() {
    const parts: string[] = [];
    if (glowEnabled) parts.push("Glow on — pixels draw with a bright halo.");
    if (outlineEnabled) parts.push("Outline on — painting the tint-safe Outline layer.");
    if (animEnabled) {
      parts.push(
        `Animation on — painting frame ${currentFrameIndex + 1}/${frameCount}. Static layer stays separate.`,
      );
    }
    if (parts.length) status.textContent = parts.join(" ");
  }

  function stopPlayLoop() {
    if (playRaf) {
      cancelAnimationFrame(playRaf);
      playRaf = 0;
    }
    playLast = 0;
  }

  function tickPlay(ts: number) {
    if (!playEnabled || !animEnabled) {
      stopPlayLoop();
      return;
    }
    if (!playLast || ts - playLast >= ANIM_FRAME_MS) {
      playLast = ts;
      playFrameIndex = (playFrameIndex + 1) % frameCount;
      redrawEditor();
    }
    playRaf = requestAnimationFrame(tickPlay);
  }

  function startPlayLoop() {
    stopPlayLoop();
    playFrameIndex = currentFrameIndex;
    playRaf = requestAnimationFrame(tickPlay);
  }

  function setFrameCount(n: number) {
    const next = clamp(Math.round(n), 1, MAX_ANIM_FRAMES);
    if (next === frameCount) return;
    ensureAnimFrameCanvases(next);
    frameCount = next;
    frameCountInput.value = String(frameCount);
    if (currentFrameIndex >= frameCount) currentFrameIndex = frameCount - 1;
    hideDuplicateFramePicker();
    updateFrameDisplay();
    pushHistory();
    redrawEditor();
  }

  ensureAnimFrameCanvases(frameCount);

  let zoom = 8;
  let tool: Tool = "pencil";
  let outlineEnabled = false;
  let drawing = false;
  let outlineStrokePreview = false;
  let outlineStrokeFinalColor: string | null = null;
  const outlineStrokeKeys = new Set<string>();
  let lineStart: { x: number; y: number } | null = null;
  let snapshot: LayerSnap | null = null;
  const history: LayerSnap[] = [];
  let historyIndex = -1;
  const MAX_HISTORY = 50;

  let floating: FloatingSelection | null = null;
  let selectMode: "marquee" | "move" | null = null;
  let marqueeStart: { x: number; y: number } | null = null;
  let marqueeEnd: { x: number; y: number } | null = null;
  let moveGrab: { x: number; y: number; originX: number; originY: number } | null = null;

  function captureLayers(): LayerSnap {
    ensureAnimFrameCanvases(frameCount);
    return {
      outline: outlineCtx.getImageData(0, 0, artW, artH),
      activeLayerIndex,
      layers: paintLayers.map((layer) => ({
        id: layer.id,
        name: layer.name,
        main: layer.ctx.getImageData(0, 0, artW, artH),
        matchMask: layer.matchCtx.getImageData(0, 0, artW, artH),
      })),
      animFrames: animCtxs
        .slice(0, frameCount)
        .map((ctx) => ctx.getImageData(0, 0, artW, artH)),
      matchMaskAnimFrames: matchMaskAnimCtxs
        .slice(0, frameCount)
        .map((ctx) => ctx.getImageData(0, 0, artW, artH)),
    };
  }

  function restoreLayers(snap: LayerSnap) {
    outlineCtx.putImageData(snap.outline, 0, 0);
    if (snap.layers?.length) {
      paintLayers = snap.layers.map((ls) => {
        const layer = makePaintLayer(ls.name);
        layer.id = ls.id;
        layer.name = ls.name;
        layer.ctx.putImageData(ls.main, 0, 0);
        layer.matchCtx.putImageData(ls.matchMask, 0, 0);
        return layer;
      });
      nextPaintLayerId =
        Math.max(0, ...paintLayers.map((l) => l.id)) + 1;
      activeLayerIndex = clamp(snap.activeLayerIndex ?? 0, 0, paintLayers.length - 1);
    } else {
      resetPaintLayersToOne();
    }
    syncLayersUi();
    const n = snap.animFrames.length;
    if (n > 0) {
      frameCount = clamp(n, 1, MAX_ANIM_FRAMES);
      frameCountInput.value = String(frameCount);
      ensureAnimFrameCanvases(frameCount);
      for (let i = 0; i < frameCount; i++) {
        const data = snap.animFrames[i];
        if (data) animCtxs[i].putImageData(data, 0, 0);
        else animCtxs[i].clearRect(0, 0, artW, artH);
        const md = snap.matchMaskAnimFrames?.[i];
        if (md) matchMaskAnimCtxs[i].putImageData(md, 0, 0);
        else matchMaskAnimCtxs[i].clearRect(0, 0, artW, artH);
      }
      if (currentFrameIndex >= frameCount) currentFrameIndex = frameCount - 1;
      updateFrameDisplay();
    }
  }

  function paintsOutlineLayer() {
    return (
      outlineEnabled &&
      (tool === "pencil" || tool === "line" || tool === "bucket" || tool === "eraser")
    );
  }

  function outlinePaintPreviewColor(hex: string): string {
    const [r, g, b] = hexToRgba(hex);
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    return lum <= 32 ? shadeHexColor(hex, 0.35) : shadeHexColor(hex, -0.35);
  }

  function beginOutlineStrokePreview(finalColor: string | null) {
    outlineStrokeKeys.clear();
    outlineStrokePreview = !!finalColor && paintsOutlineLayer();
    outlineStrokeFinalColor = outlineStrokePreview ? finalColor : null;
  }

  function resetOutlineStrokePreview() {
    outlineStrokePreview = false;
    outlineStrokeFinalColor = null;
    outlineStrokeKeys.clear();
  }

  function trackOutlineStrokePixel(x: number, y: number) {
    outlineStrokeKeys.add(`${x},${y}`);
  }

  function commitOutlineStrokePreview() {
    if (!outlineStrokePreview || !outlineStrokeFinalColor || outlineStrokeKeys.size === 0) {
      resetOutlineStrokePreview();
      return;
    }
    const useGlow = glowEnabled;
    for (const key of outlineStrokeKeys) {
      const [xs, ys] = key.split(",");
      const x = Number(xs);
      const y = Number(ys);
      paintPixel(outlineCtx, x, y, outlineStrokeFinalColor, {
        glow: useGlow,
        canvasW: artW,
        canvasH: artH,
      });
    }
    resetOutlineStrokePreview();
  }

  function activePaintLayer(): "main" | "outline" | "both" {
    if (tool === "eraser") {
      if (outlineEnabled) return "outline";
      return animEnabled ? "main" : "both";
    }
    if (paintsOutlineLayer()) return "outline";
    return "main";
  }

  function syncUndoRedoBtns() {
    undoBtn.disabled = historyIndex <= 0;
    redoBtn.disabled = historyIndex < 0 || historyIndex >= history.length - 1;
  }

  function pushHistory() {
    const snap = captureLayers();
    history.splice(historyIndex + 1);
    history.push(snap);
    while (history.length > MAX_HISTORY) {
      history.shift();
      historyIndex = Math.max(0, historyIndex - 1);
    }
    historyIndex = history.length - 1;
    syncUndoRedoBtns();
  }

  function undo() {
    if (historyIndex <= 0) return;
    floating = null;
    marqueeStart = null;
    marqueeEnd = null;
    selectMode = null;
    historyIndex -= 1;
    restoreLayers(history[historyIndex]);
    redrawEditor();
    syncUndoRedoBtns();
  }

  function redo() {
    if (historyIndex >= history.length - 1) return;
    floating = null;
    marqueeStart = null;
    marqueeEnd = null;
    selectMode = null;
    historyIndex += 1;
    restoreLayers(history[historyIndex]);
    redrawEditor();
    syncUndoRedoBtns();
  }

  function stampFloating() {
    if (!floating) return;
    getMainPaintCtx().putImageData(floating.pixels, floating.x, floating.y);
    getMatchMaskPaintCtx().putImageData(floating.matchPixels, floating.x, floating.y);
    floating = null;
    syncSelectActionsUi();
  }

  function commitFloating() {
    if (!floating) return;
    stampFloating();
    pushHistory();
  }

  function duplicateFloatingSelection() {
    if (!floating) return;
    // Leave a stamped copy, keep a floating duplicate offset for placement.
    getMainPaintCtx().putImageData(floating.pixels, floating.x, floating.y);
    getMatchMaskPaintCtx().putImageData(floating.matchPixels, floating.x, floating.y);
    const pixels = getMainPaintCtx().createImageData(floating.w, floating.h);
    pixels.data.set(floating.pixels.data);
    const matchPixels = getMatchMaskPaintCtx().createImageData(floating.w, floating.h);
    matchPixels.data.set(floating.matchPixels.data);
    floating = {
      x: clamp(floating.x + 2, 0, artW - floating.w),
      y: clamp(floating.y + 2, 0, artH - floating.h),
      w: floating.w,
      h: floating.h,
      pixels,
      matchPixels,
    };
    pushHistory();
    syncSelectActionsUi();
    redrawEditor();
    status.textContent = "Selection duplicated — drag to place.";
  }

  function applyImportedImageData(pixels: ImageData) {
    const w = pixels.width;
    const h = pixels.height;
    if (w < 1 || h < 1 || w > artW || h > artH) {
      status.textContent = "Imported image does not fit the canvas.";
      return;
    }
    if (floating) commitFloating();
    marqueeStart = null;
    marqueeEnd = null;
    selectMode = null;
    moveGrab = null;

    const matchPixels = getMatchMaskPaintCtx().createImageData(w, h);
    // Center over the reference cursor (canvas midpoint).
    const x = clamp(Math.round(artW / 2 - w / 2), 0, artW - w);
    const y = clamp(Math.round(artH / 2 - h / 2), 0, artH - h);
    floating = { x, y, w, h, pixels, matchPixels };

    tool = "select";
    toolSelect.value = "select";
    syncSelectActionsUi();
    redrawEditor();
    status.textContent = `Imported ${w}×${h} — drag to place, then click outside to stamp.`;
  }

  function openImportPreviewModal(img: HTMLImageElement, fileName: string) {
    const maxDim = Math.min(artW, artH);
    const resolutions = importResolutionOptions(maxDim);
    let chosen =
      resolutions.find((n) => n === 32) ??
      resolutions.find((n) => n === 64) ??
      resolutions[Math.min(2, resolutions.length - 1)] ??
      maxDim;

    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal adminShopImportModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox adminShopImportModalPanel";
    box.innerHTML = `<h2>Import image</h2>
      <p class="accessHint">Preview how the image will look after downscaling. Choose a resolution, then Continue to place it on the canvas.</p>`;

    const meta = document.createElement("p");
    meta.className = "accessHint adminShopImportMeta";
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    meta.textContent = `${fileName || "image"} — source ${srcW}×${srcH}`;
    box.appendChild(meta);

    const previewWrap = document.createElement("div");
    previewWrap.className = "adminShopImportPreviewWrap";
    const previewCanvas = document.createElement("canvas");
    previewCanvas.className = "adminShopImportPreviewCanvas";
    previewCanvas.width = chosen;
    previewCanvas.height = chosen;
    previewWrap.appendChild(previewCanvas);
    box.appendChild(previewWrap);

    const resRow = document.createElement("div");
    resRow.className = "formRow";
    const resLab = document.createElement("span");
    resLab.className = "formLabel";
    resLab.textContent = "Resolution";
    const resSelect = document.createElement("select");
    resSelect.className = "formInput adminShopToolSelect";
    resSelect.setAttribute("aria-label", "Import resolution");
    resolutions.forEach((n) => {
      const o = document.createElement("option");
      o.value = String(n);
      o.textContent = `${n}×${n}`;
      resSelect.appendChild(o);
    });
    resSelect.value = String(chosen);
    resRow.appendChild(resLab);
    resRow.appendChild(resSelect);
    box.appendChild(resRow);

    const resSlider = document.createElement("input");
    resSlider.type = "range";
    resSlider.min = "0";
    resSlider.max = String(Math.max(0, resolutions.length - 1));
    resSlider.step = "1";
    resSlider.value = String(Math.max(0, resolutions.indexOf(chosen)));
    resSlider.className = "adminShopImportResSlider";
    resSlider.setAttribute("aria-label", "Import resolution slider");
    box.appendChild(resSlider);

    const resHint = document.createElement("p");
    resHint.className = "accessHint adminShopImportResHint";
    box.appendChild(resHint);

    const statusLine = document.createElement("p");
    statusLine.className = "accessHint";
    box.appendChild(statusLine);

    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const continueBtn = document.createElement("button");
    continueBtn.type = "button";
    continueBtn.className = "btn green";
    continueBtn.textContent = "Continue";
    actions.appendChild(cancelBtn);
    actions.appendChild(continueBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);

    const renderPreview = () => {
      const data = downscaleImageToGrid(img, chosen);
      previewCanvas.width = chosen;
      previewCanvas.height = chosen;
      const ctx = previewCanvas.getContext("2d")!;
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, chosen, chosen);
      ctx.putImageData(data, 0, 0);
      const displayPx = Math.min(256, Math.max(96, chosen * 4));
      previewCanvas.style.width = `${displayPx}px`;
      previewCanvas.style.height = `${displayPx}px`;
      resHint.textContent = `${chosen}×${chosen} pixels (fits on ${artW}×${artH} canvas)`;
    };

    const setResolution = (n: number) => {
      chosen = n;
      resSelect.value = String(n);
      const idx = resolutions.indexOf(n);
      if (idx >= 0) resSlider.value = String(idx);
      renderPreview();
    };

    resSelect.onchange = () => {
      const n = Number(resSelect.value) || chosen;
      setResolution(n);
    };
    resSlider.oninput = () => {
      const idx = clamp(Number(resSlider.value) || 0, 0, resolutions.length - 1);
      setResolution(resolutions[idx]!);
    };

    const close = () => modal.remove();
    cancelBtn.onclick = () => close();
    continueBtn.onclick = () => {
      const data = downscaleImageToGrid(img, chosen);
      applyImportedImageData(data);
      close();
    };

    renderPreview();
  }

  function clearRectTransparent(x: number, y: number, w: number, h: number) {
    getMainPaintCtx().clearRect(x, y, w, h);
    getMatchMaskPaintCtx().clearRect(x, y, w, h);
  }

  function drawSymmetryCross(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
  ) {
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.strokeStyle = "rgba(255, 220, 80, 0.92)";
    ctx.lineWidth = Math.max(1, radius * 0.06);
    ctx.setLineDash([]);
    // Four rays from center through the cursor circle (and a bit beyond).
    const span = radius + Math.max(2, radius * 0.35);
    ctx.beginPath();
    ctx.moveTo(cx, cy - span);
    ctx.lineTo(cx, cy + span);
    ctx.moveTo(cx - span, cy);
    ctx.lineTo(cx + span, cy);
    ctx.stroke();
    ctx.strokeStyle = "rgba(0, 0, 0, 0.45)";
    ctx.lineWidth = Math.max(0.6, radius * 0.03);
    ctx.setLineDash([Math.max(1.5, radius * 0.12), Math.max(1.5, radius * 0.1)]);
    ctx.beginPath();
    ctx.moveTo(cx, cy - span);
    ctx.lineTo(cx, cy + span);
    ctx.moveTo(cx - span, cy);
    ctx.lineTo(cx + span, cy);
    ctx.stroke();
    ctx.restore();
  }

  function redrawEditor() {
    const z = zoom;
    if (cosmeticKind === "banner") {
      // Fit the full banner in the viewport — no horizontal/vertical scroll.
      const parent = canvas.parentElement;
      const availW = Math.max(160, (parent?.clientWidth || 640) - 16);
      const availH = Math.max(48, Math.min(parent?.clientHeight || 180, 220));
      const fit = Math.min(availW / artW, availH / artH);
      canvas.style.width = `${Math.floor(artW * fit)}px`;
      canvas.style.height = `${Math.floor(artH * fit)}px`;
    } else {
      canvas.style.width = `${artW * z}px`;
      canvas.style.height = `${artH * z}px`;
    }
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = canvasBgColor.value || "#1a1a22";
    ctx.fillRect(0, 0, artW, artH);
    if (cosmeticKind === "cursor") {
      if (newCursorCheck.checked) {
        drawReferenceCursorHitboxOutline(
          ctx,
          artW / 2,
          artH / 2,
          REF_CURSOR_R,
        );
      } else if (refCheck.checked) {
        drawReferenceCursor(
          ctx,
          artW / 2,
          artH / 2,
          REF_CURSOR_R,
          cursorGuideColor.value || "#ffffff",
        );
      }
      if (symmetryCheck.checked) {
        drawSymmetryCross(ctx, artW / 2, artH / 2, REF_CURSOR_R);
      }
    }
    compositePaintLayersOnto(ctx);
    if (animEnabled && frameCount > 0) {
      const fi = editorDisplayFrameIndex();
      if (animOffs[fi]) ctx.drawImage(animOffs[fi], 0, 0);
    }
    ctx.drawImage(outlineOff, 0, 0);
    if (floating) {
      ctx.putImageData(floating.pixels, floating.x, floating.y);
      ctx.save();
      ctx.strokeStyle = "rgba(255,255,255,.95)";
      ctx.lineWidth = 1 / z;
      ctx.setLineDash([2 / z, 2 / z]);
      ctx.strokeRect(floating.x + 0.5 / z, floating.y + 0.5 / z, floating.w, floating.h);
      ctx.restore();
    } else if (marqueeStart && marqueeEnd) {
      const x0 = Math.min(marqueeStart.x, marqueeEnd.x);
      const y0 = Math.min(marqueeStart.y, marqueeEnd.y);
      const w = Math.abs(marqueeEnd.x - marqueeStart.x) + 1;
      const h = Math.abs(marqueeEnd.y - marqueeStart.y) + 1;
      ctx.save();
      ctx.strokeStyle = "rgba(102,255,255,.95)";
      ctx.lineWidth = 1 / z;
      ctx.setLineDash([2 / z, 2 / z]);
      ctx.strokeRect(x0 + 0.5 / z, y0 + 0.5 / z, w, h);
      ctx.restore();
    }
    syncSelectActionsUi();
  }

  let particlePreviewUrl = TRAIL_PLACEHOLDER_IMAGE;
  let particlePreviewFrames: string[] | null = null;

  function refreshParticlePreviewCache() {
    if (cosmeticKind !== "trail" || trailMode !== "particle") return;
    try {
      particlePreviewUrl = exportDataUrl();
      particlePreviewFrames = animEnabled ? exportAnimationFrames() : null;
    } catch {
      particlePreviewUrl = TRAIL_PLACEHOLDER_IMAGE;
      particlePreviewFrames = null;
    }
  }

  function currentTrailVisual() {
    return {
      trailMode,
      trailColors: trailMode === "multiColor" ? [...trailColors] : null,
      imageData: trailMode === "particle" ? particlePreviewUrl : TRAIL_PLACEHOLDER_IMAGE,
      animationFrames: trailMode === "particle" ? particlePreviewFrames : null,
    };
  }

  function refreshTrailGradientPreview() {
    if (!trailColors.length) {
      trailGradientPreview.style.background = "transparent";
      return;
    }
    const stops = trailColors
      .map((c, i) => `${c} ${(i / Math.max(1, trailColors.length - 1)) * 100}%`)
      .join(", ");
    trailGradientPreview.style.background = `linear-gradient(90deg, ${stops})`;
  }

  function renderTrailColorInputs() {
    trailColorsList.replaceChildren();
    trailColors.forEach((color, index) => {
      const row = document.createElement("div");
      row.className = "adminShopTrailColorRow";
      const inp = document.createElement("input");
      inp.type = "color";
      inp.className = "adminShopColorInput";
      inp.value = color;
      inp.setAttribute("aria-label", `Trail color ${index + 1}`);
      inp.oninput = () => {
        trailColors[index] = inp.value;
        refreshTrailGradientPreview();
      };
      const idx = document.createElement("span");
      idx.className = "accessHint";
      idx.textContent = String(index + 1);
      row.appendChild(idx);
      row.appendChild(inp);
      trailColorsList.appendChild(row);
    });
    refreshTrailGradientPreview();
    addTrailColorBtn.disabled = trailColors.length >= MAX_TRAIL_COLORS;
    removeTrailColorBtn.disabled = trailColors.length <= 1;
  }

  function stopTrailPreviewLoop() {
    if (trailPreviewRaf) {
      cancelAnimationFrame(trailPreviewRaf);
      trailPreviewRaf = 0;
    }
  }

  function startTrailPreviewLoop() {
    stopTrailPreviewLoop();
    if (cosmeticKind !== "trail" || !supportsTrail) return;
    trailPreviewState = createTrailPreviewState();
    trailPreviewT0 = performance.now();
    refreshParticlePreviewCache();
    const tick = () => {
      if (!root.isConnected || cosmeticKind !== "trail") {
        stopTrailPreviewLoop();
        return;
      }
      const tSec = (performance.now() - trailPreviewT0) / 1000;
      void paintTrailCardPreview(
        trailPreviewCanvas,
        currentTrailVisual(),
        tSec,
        cursorGuideColor.value || "#ffffff",
        trailPreviewState,
      ).then((st) => {
        trailPreviewState = st;
      });
      trailPreviewRaf = requestAnimationFrame(tick);
    };
    trailPreviewRaf = requestAnimationFrame(tick);
  }

  function syncCosmeticKindUi() {
    if (!supportsTrail) return;
    const isTrail = cosmeticKind === "trail";
    const isBanner = cosmeticKind === "banner";
    const isTheme = cosmeticKind === "theme";
    const isMulti = isTrail && trailMode === "multiColor";
    typeSelect.value = cosmeticKind;
    trailModeSelect.value = trailMode;
    trailModeGroup.classList.toggle("hidden", !isTrail);
    trailColorsPanel.classList.toggle("hidden", !isMulti);
    trailPreviewWrap.classList.toggle("hidden", !isTrail);
    themeEditor?.root.classList.toggle("hidden", !isTheme);
    toolsGroup.classList.toggle("hidden", isMulti || isTheme);
    colorGroup.classList.toggle("hidden", isMulti || isTheme);
    fxGroup.classList.toggle("hidden", isMulti || isTheme);
    outlineLabel.classList.toggle("hidden", isBanner || isTheme);
    createOutlineBtn.classList.toggle("hidden", isBanner || isTheme);
    matchCursorGroup.classList.toggle("hidden", isTrail || isBanner || isTheme);
    newCursorToggle.classList.toggle("hidden", isTrail || isBanner || isTheme || mode === "crateArt");
    zoomGroup.classList.toggle("hidden", isMulti || isBanner || isTheme);
    guideGroup.classList.toggle("hidden", isMulti || isBanner || isTheme);
    workspace.classList.toggle("hidden", isMulti || isTheme);
    layersPanel.classList.toggle("hidden", isMulti || isTheme);
    canvas.classList.toggle("adminShopCreatorCanvas--wide", isBanner);
    syncToolSelectOptions();
    if (isTrail || isTheme) {
      refCheck.checked = false;
      matchCursorCheck.checked = false;
      newCursorCheck.checked = false;
      symmetryCheck.checked = false;
      syncMatchCursorUi();
    }
    if (isBanner) {
      refCheck.checked = false;
      matchCursorCheck.checked = false;
      newCursorCheck.checked = false;
      symmetryCheck.checked = false;
      outlineEnabled = false;
      outlineCheck.checked = false;
      syncMatchCursorUi();
    }
    if (!isTheme) {
      resizeArtBuffers(isBanner ? BANNER_ART_W : CANVAS_SIZE, isBanner ? BANNER_ART_H : CANVAS_SIZE);
    }
    if (isMulti) renderTrailColorInputs();
    if (isTrail && trailMode === "particle") refreshParticlePreviewCache();
    redrawEditor();
    if (isTrail) startTrailPreviewLoop();
    else stopTrailPreviewLoop();
  }

  function pointInFloating(x: number, y: number) {
    if (!floating) return false;
    return (
      x >= floating.x &&
      y >= floating.y &&
      x < floating.x + floating.w &&
      y < floating.y + floating.h
    );
  }

  function toCell(e: MouseEvent | Touch) {
    const r = canvas.getBoundingClientRect();
    const x = Math.floor(((e.clientX - r.left) / r.width) * artW);
    const y = Math.floor(((e.clientY - r.top) / r.height) * artH);
    return { x: clamp(x, 0, artW - 1), y: clamp(y, 0, artH - 1) };
  }

  function setPixel(x: number, y: number, color: string | null, layer = activePaintLayer()) {
    const useGlow = !!color && glowEnabled;
    const s = pencilSize;
    const half = Math.floor(s / 2);
    for (let dy = 0; dy < s; dy++) {
      for (let dx = 0; dx < s; dx++) {
        const px = clamp(x - half + dx, 0, artW - 1);
        const py = clamp(y - half + dy, 0, artH - 1);
        if (layer === "both") {
          paintPixel(activePaintLayerObj().ctx, px, py, color, { glow: useGlow, canvasW: artW, canvasH: artH });
          paintPixel(outlineCtx, px, py, color, { glow: useGlow, canvasW: artW, canvasH: artH });
          if (!color) stampMatchMaskAt(px, py, null, useGlow);
        } else if (layer === "outline") {
          let paintColor = color;
          if (color && outlineStrokePreview) {
            paintColor = outlinePaintPreviewColor(color);
            trackOutlineStrokePixel(px, py);
          }
          paintPixel(outlineCtx, px, py, paintColor, { glow: useGlow, canvasW: artW, canvasH: artH });
        } else {
          const ctx = getMainPaintCtx();
          paintPixel(ctx, px, py, color, { glow: useGlow, canvasW: artW, canvasH: artH });
          if (!color) {
            stampMatchMaskAt(px, py, null, useGlow);
          } else if (matchCursorCheck.checked) {
            stampMatchMaskAt(px, py, matchCursorShade(), useGlow);
          } else {
            stampMatchMaskAt(px, py, null, useGlow);
          }
        }
      }
    }
  }

  /**
   * Stamp normal readable text onto the active paint layer.
   * Banner text stays editable before stamping via the text modal controls.
   */
  function stampText(
    x: number,
    y: number,
    text: string,
    color: string,
    size: number,
    rotationDeg: number,
  ) {
    if (!text) return;
    const ctx = getMainPaintCtx();
    const fontSize = Math.max(8, Math.floor(size));
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.translate(x, y);
    ctx.rotate((rotationDeg * Math.PI) / 180);
    ctx.font = `700 ${fontSize}px Arial, Helvetica, sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(text, 0, 0);
    ctx.restore();
  }

  function openBannerTextModal(x: number, y: number) {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox adminShopTextModal";
    box.innerHTML = `<h2>Add text</h2>
      <p class="accessHint">Use normal text, choose its color, and adjust position, rotation, and size before stamping it onto the banner.</p>`;

    const row = (label: string, el: HTMLElement) => {
      const wrap = document.createElement("div");
      wrap.className = "formRow";
      const lab = document.createElement("span");
      lab.className = "formLabel";
      lab.textContent = label;
      wrap.appendChild(lab);
      wrap.appendChild(el);
      box.appendChild(wrap);
      return wrap;
    };

    const textInput = document.createElement("input");
    textInput.type = "text";
    textInput.className = "formInput";
    textInput.maxLength = 60;
    textInput.placeholder = "Banner text";
    row("Text", textInput);

    const textColorInput = document.createElement("input");
    textColorInput.type = "color";
    textColorInput.className = "adminShopColorInput";
    textColorInput.value = currentDrawColor();
    row("Color", textColorInput);

    const posGrid = document.createElement("div");
    posGrid.className = "adminShopTextModalGrid";
    const posX = document.createElement("input");
    posX.type = "number";
    posX.className = "formInput";
    posX.step = "1";
    posX.min = "0";
    posX.max = String(Math.max(0, artW - 1));
    posX.value = String(x);
    const posY = document.createElement("input");
    posY.type = "number";
    posY.className = "formInput";
    posY.step = "1";
    posY.min = "0";
    posY.max = String(Math.max(0, artH - 1));
    posY.value = String(y);
    posGrid.appendChild(posX);
    posGrid.appendChild(posY);
    row("X / Y", posGrid);

    const sizeInput = document.createElement("input");
    sizeInput.type = "number";
    sizeInput.className = "formInput";
    sizeInput.step = "1";
    sizeInput.min = "8";
    sizeInput.max = String(Math.max(16, artH * 2));
    sizeInput.value = String(Math.max(12, Math.round(artH * 0.5)));
    row("Size", sizeInput);

    const rotationInput = document.createElement("input");
    rotationInput.type = "number";
    rotationInput.className = "formInput";
    rotationInput.step = "1";
    rotationInput.min = "-360";
    rotationInput.max = "360";
    rotationInput.value = "0";
    row("Rotation", rotationInput);

    const statusLine = document.createElement("p");
    statusLine.className = "accessHint";
    box.appendChild(statusLine);

    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const createBtn = document.createElement("button");
    createBtn.type = "button";
    createBtn.className = "btn green";
    createBtn.textContent = "Stamp";
    actions.appendChild(cancelBtn);
    actions.appendChild(createBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);

    const close = () => modal.remove();
    cancelBtn.onclick = () => close();
    createBtn.onclick = () => {
      const text = textInput.value.trim();
      if (!text) {
        statusLine.textContent = "Enter text first.";
        return;
      }
      const tx = clamp(Math.round(Number(posX.value) || 0), 0, artW - 1);
      const ty = clamp(Math.round(Number(posY.value) || 0), 0, artH - 1);
      const size = clamp(Math.round(Number(sizeInput.value) || 0), 8, Math.max(16, artH * 2));
      const rotation = Number(rotationInput.value) || 0;
      const color = textColorInput.value || currentDrawColor();
      stampText(tx, ty, text, color, size, rotation);
      pushHistory();
      redrawEditor();
      refreshParticlePreviewCache();
      status.textContent = `Stamped text "${text}".`;
      close();
    };

    textInput.focus();
    textInput.select();
  }

  /** Edge pixels of the reference cursor disk — stamp onto main art or Outline layer. */
  function stampCursorGuideOutline(color: string, layerKind: "main" | "outline") {
    if (floating) commitFloating();
    const cx = artW / 2;
    const cy = artH / 2;
    const r = REF_CURSOR_R;
    const r2 = r * r;
    const inside = (x: number, y: number) => {
      if (x < 0 || y < 0 || x >= artW || y >= artH) return false;
      const dx = x + 0.5 - cx;
      const dy = y + 0.5 - cy;
      return dx * dx + dy * dy <= r2;
    };
    const layer = layerKind === "outline" ? "outline" : "main";
    for (let y = 0; y < artH; y++) {
      for (let x = 0; x < artW; x++) {
        if (!inside(x, y)) continue;
        if (inside(x - 1, y) && inside(x + 1, y) && inside(x, y - 1) && inside(x, y + 1)) continue;
        setPixel(x, y, color, layer);
      }
    }
    pushHistory();
    redrawEditor();
  }

  function openCreateOutlineModal() {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>Create Outline</h2>
      <p class="accessHint">Stamp a perfect outline of the cursor guide onto the main art or the Outline layer (tint-safe rim).</p>`;

    const colorInput = document.createElement("input");
    colorInput.type = "color";
    colorInput.className = "adminShopColorInput";
    colorInput.value = "#1a1a1a";
    colorInput.setAttribute("aria-label", "Outline color");

    const layerSelect = document.createElement("select");
    layerSelect.className = "formInput";
    layerSelect.setAttribute("aria-label", "Layer");
    (
      [
        ["outline", "Outline layer"],
        ["main", "Main art"],
      ] as const
    ).forEach(([value, label]) => {
      const o = document.createElement("option");
      o.value = value;
      o.textContent = label;
      layerSelect.appendChild(o);
    });
    layerSelect.value = "outline";

    const colorRow = document.createElement("div");
    colorRow.className = "formRow";
    const colorLab = document.createElement("span");
    colorLab.className = "formLabel";
    colorLab.textContent = "Color";
    colorRow.appendChild(colorLab);
    colorRow.appendChild(colorInput);
    box.appendChild(colorRow);

    const layerRow = document.createElement("div");
    layerRow.className = "formRow";
    const layerLab = document.createElement("span");
    layerLab.className = "formLabel";
    layerLab.textContent = "Layer";
    layerRow.appendChild(layerLab);
    layerRow.appendChild(layerSelect);
    box.appendChild(layerRow);

    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const createBtn = document.createElement("button");
    createBtn.type = "button";
    createBtn.className = "btn green";
    createBtn.textContent = "Create";
    actions.appendChild(cancelBtn);
    actions.appendChild(createBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);

    cancelBtn.onclick = () => modal.remove();
    createBtn.onclick = () => {
      const layerKind = layerSelect.value === "main" ? "main" : "outline";
      stampCursorGuideOutline(colorInput.value || "#1a1a1a", layerKind);
      modal.remove();
      status.textContent =
        layerKind === "main"
          ? "Cursor outline stamped on main art. Undo available."
          : "Cursor outline stamped on Outline layer. Undo available.";
    };
  }

  createOutlineBtn.onclick = () => openCreateOutlineModal();

  /** Visible composite: outline on top, then anim frame, then static main. */
  function visibleRgba(
    main: ImageData,
    outline: ImageData,
    anim: ImageData | null,
    x: number,
    y: number,
  ): [number, number, number, number] {
    const i = (y * artW + x) * 4;
    if (outline.data[i + 3] > 0) {
      return [outline.data[i], outline.data[i + 1], outline.data[i + 2], outline.data[i + 3]];
    }
    if (anim && anim.data[i + 3] > 0) {
      return [anim.data[i], anim.data[i + 1], anim.data[i + 2], anim.data[i + 3]];
    }
    return [main.data[i], main.data[i + 1], main.data[i + 2], main.data[i + 3]];
  }

  /** Visible composite: outline sits on top of main (matches what the editor shows). */
  function compositeRgba(
    main: ImageData,
    outline: ImageData,
    x: number,
    y: number,
  ): [number, number, number, number] {
    const i = (y * artW + x) * 4;
    if (outline.data[i + 3] > 0) {
      return [outline.data[i], outline.data[i + 1], outline.data[i + 2], outline.data[i + 3]];
    }
    return [main.data[i], main.data[i + 1], main.data[i + 2], main.data[i + 3]];
  }

  function setRgba(img: ImageData, x: number, y: number, rgba: [number, number, number, number]) {
    const i = (y * artW + x) * 4;
    img.data[i] = rgba[0];
    img.data[i + 1] = rgba[1];
    img.data[i + 2] = rgba[2];
    img.data[i + 3] = rgba[3];
  }

  /**
   * Flood-fill the active paint layer. Outline pixels act as walls when filling main art.
   */
  function floodFill(sx: number, sy: number, fill: string) {
    const outline = outlineCtx.getImageData(0, 0, artW, artH);
    const oi = (sy * artW + sx) * 4;
    if (!outlineEnabled && outline.data[oi + 3] > 0) return;

    const fillRgba = hexToRgba(fill);
    const filled: Array<[number, number]> = [];

    if (animEnabled && !outlineEnabled) {
      const animCtx = getMainPaintCtx();
      const anim = animCtx.getImageData(0, 0, artW, artH);
      const ai = oi;
      const target: [number, number, number, number] = [
        anim.data[ai],
        anim.data[ai + 1],
        anim.data[ai + 2],
        anim.data[ai + 3],
      ];
      if (sameRgba(target, fillRgba)) return;

      const stack: Array<[number, number]> = [[sx, sy]];
      const seen = new Uint8Array(artW * artH);
      while (stack.length) {
        const [x, y] = stack.pop()!;
        const idx = y * artW + x;
        if (seen[idx]) continue;
        seen[idx] = 1;
        const pi = idx * 4;
        if (outline.data[pi + 3] > 0) continue;
        const cur: [number, number, number, number] = [
          anim.data[pi],
          anim.data[pi + 1],
          anim.data[pi + 2],
          anim.data[pi + 3],
        ];
        if (!sameRgba(cur, target)) continue;
        filled.push([x, y]);
        if (x > 0) stack.push([x - 1, y]);
        if (x < artW - 1) stack.push([x + 1, y]);
        if (y > 0) stack.push([x, y - 1]);
        if (y < artH - 1) stack.push([x, y + 1]);
      }
      applyFloodPixels(getMainPaintCtx(), filled, fill, fillRgba);
      return;
    }

    const main = getMainPaintCtx().getImageData(0, 0, artW, artH);
    const target = compositeRgba(main, outline, sx, sy);
    if (sameRgba(target, fillRgba)) return;

    const stack: Array<[number, number]> = [[sx, sy]];
    const seen = new Uint8Array(artW * artH);
    while (stack.length) {
      const [x, y] = stack.pop()!;
      const idx = y * artW + x;
      if (seen[idx]) continue;
      seen[idx] = 1;
      if (!sameRgba(compositeRgba(main, outline, x, y), target)) continue;
      filled.push([x, y]);
      if (x > 0) stack.push([x - 1, y]);
      if (x < artW - 1) stack.push([x + 1, y]);
      if (y > 0) stack.push([x, y - 1]);
      if (y < artH - 1) stack.push([x, y + 1]);
    }
    if (outlineEnabled) {
      applyFloodPixels(outlineCtx, filled, fill, fillRgba, { skipMatchMask: true });
    } else {
      applyFloodPixels(getMainPaintCtx(), filled, fill, fillRgba);
    }
  }

  function applyFloodPixels(
    ctx: CanvasRenderingContext2D,
    pixels: Array<[number, number]>,
    fill: string,
    fillRgba: [number, number, number, number],
    opts?: { skipMatchMask?: boolean },
  ) {
    if (!pixels.length) return;
    if (glowEnabled) {
      for (const [x, y] of pixels) {
        paintPixel(ctx, x, y, fill, { glow: true, canvasW: artW, canvasH: artH });
      }
    } else {
      const img = ctx.getImageData(0, 0, artW, artH);
      for (const [x, y] of pixels) {
        setRgba(img, x, y, fillRgba);
      }
      ctx.putImageData(img, 0, 0);
    }
    if (opts?.skipMatchMask) return;
    const shade = matchCursorCheck.checked ? matchCursorShade() : null;
    for (const [x, y] of pixels) {
      stampMatchMaskAt(x, y, shade, glowEnabled && shade != null);
    }
  }

  function hexToRgba(hex: string): [number, number, number, number] {
    const h = hex.replace("#", "");
    const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 255];
  }

  function rgbaToHex(r: number, g: number, b: number): string {
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
  }

  function sameRgba(
    a: [number, number, number, number],
    b: [number, number, number, number],
  ) {
    return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
  }

  function pickColorAt(sx: number, sy: number): string | null {
    const main = getCompositeMainImageData();
    const outline = outlineCtx.getImageData(0, 0, artW, artH);
    const anim =
      animEnabled && animCtxs[currentFrameIndex]
        ? animCtxs[currentFrameIndex].getImageData(0, 0, artW, artH)
        : null;
    const [r, g, b, a] = visibleRgba(main, outline, anim, sx, sy);
    if (a <= 0) return null;
    return rgbaToHex(r, g, b);
  }

  function resetEditor() {
    floating = null;
    marqueeStart = null;
    marqueeEnd = null;
    selectMode = null;
    moveGrab = null;
    lineStart = null;
    snapshot = null;
    drawing = false;
    editingItem = null;
    resetPaintLayersToOne();
    syncLayersUi();
    outlineCtx.clearRect(0, 0, artW, artH);
    glowEnabled = false;
    animEnabled = false;
    playEnabled = false;
    glowCheck.checked = false;
    animCheck.checked = false;
    playCheck.checked = false;
    stopPlayLoop();
    frameCount = 4;
    currentFrameIndex = 0;
    playFrameIndex = 0;
    frameCountInput.value = "4";
    ensureAnimFrameCanvases(frameCount);
    clearAllAnimFrames();
    syncAnimControls();
    history.length = 0;
    historyIndex = -1;
    tool = "pencil";
    toolSelect.value = "pencil";
    outlineEnabled = false;
    outlineCheck.checked = false;
    colorInput.value = "#ff66ff";
    zoom = 8;
    zoomSelect.value = "8";
    cursorGuideColor.value = "#ffffff";
    canvasBgColor.value = "#1a1a22";
    refCheck.checked = true;
    newCursorCheck.checked = false;
    symmetryCheck.checked = false;
    matchCursorCheck.checked = false;
    matchShadeInput.value = "0";
    syncMatchCursorUi();
    canvas.style.cursor = "crosshair";
    pushHistory();
    redrawEditor();
    status.textContent = "Editor reset.";
  }

  function drawLine(x0: number, y0: number, x1: number, y1: number, color: string | null) {
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;
    let x = x0;
    let y = y0;
    while (true) {
      setPixel(x, y, color);
      if (x === x1 && y === y1) break;
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
  }

  function pointerDown(e: MouseEvent | TouchEvent) {
    e.preventDefault();
    const pt = "touches" in e ? e.touches[0] : e;
    if (!pt) return;
    const { x, y } = toCell(pt);
    drawing = true;
    const color = tool === "eraser" ? null : currentDrawColor();

    if (tool === "select") {
      if (floating && pointInFloating(x, y)) {
        selectMode = "move";
        moveGrab = { x, y, originX: floating.x, originY: floating.y };
        return;
      }
      if (floating) {
        commitFloating();
      }
      selectMode = "marquee";
      marqueeStart = { x, y };
      marqueeEnd = { x, y };
      redrawEditor();
      return;
    }

    if (floating) {
      commitFloating();
      redrawEditor();
    }

    if (tool === "bucket") {
      floodFill(x, y, currentDrawColor());
      pushHistory();
      redrawEditor();
      drawing = false;
      return;
    }
    if (tool === "eyedropper") {
      const picked = pickColorAt(x, y);
      if (picked) {
        if (matchCursorCheck.checked) {
          matchCursorCheck.checked = false;
          syncMatchCursorUi();
        }
        colorInput.value = picked;
        status.textContent = `Picked ${picked}`;
      } else {
        status.textContent = "Empty pixel — no color.";
      }
      drawing = false;
      return;
    }
    if (tool === "text") {
      drawing = false;
      openBannerTextModal(x, y);
      return;
    }
    if (tool === "line") {
      beginOutlineStrokePreview(color);
      lineStart = { x, y };
      snapshot = captureLayers();
      return;
    }
    beginOutlineStrokePreview(color);
    setPixel(x, y, color);
    redrawEditor();
  }

  function pointerMove(e: MouseEvent | TouchEvent) {
    if (!drawing) return;
    e.preventDefault();
    const pt = "touches" in e ? e.touches[0] : e;
    if (!pt) return;
    const { x, y } = toCell(pt);
    const color = tool === "eraser" ? null : currentDrawColor();

    if (tool === "select") {
      if (selectMode === "marquee" && marqueeStart) {
        marqueeEnd = { x, y };
        redrawEditor();
        return;
      }
      if (selectMode === "move" && floating && moveGrab) {
        const dx = x - moveGrab.x;
        const dy = y - moveGrab.y;
        floating.x = clamp(moveGrab.originX + dx, 0, artW - floating.w);
        floating.y = clamp(moveGrab.originY + dy, 0, artH - floating.h);
        redrawEditor();
        return;
      }
    }

    if (tool === "line" && lineStart && snapshot) {
      restoreLayers(snapshot);
      drawLine(lineStart.x, lineStart.y, x, y, color);
      redrawEditor();
      return;
    }
    if (tool === "pencil" || tool === "eraser") {
      setPixel(x, y, color);
      redrawEditor();
    }
  }

  function pointerUp() {
    if (tool === "select" && drawing) {
      if (selectMode === "marquee" && marqueeStart && marqueeEnd) {
        const x0 = Math.min(marqueeStart.x, marqueeEnd.x);
        const y0 = Math.min(marqueeStart.y, marqueeEnd.y);
        const w = Math.abs(marqueeEnd.x - marqueeStart.x) + 1;
        const h = Math.abs(marqueeEnd.y - marqueeStart.y) + 1;
        marqueeStart = null;
        marqueeEnd = null;
        selectMode = null;
        if (w > 0 && h > 0) {
          const pixels = getMainPaintCtx().getImageData(x0, y0, w, h);
          const matchPixels = getMatchMaskPaintCtx().getImageData(x0, y0, w, h);
          let hasInk = false;
          for (let i = 3; i < pixels.data.length; i += 4) {
            if (pixels.data[i] > 0) {
              hasInk = true;
              break;
            }
          }
          if (hasInk) {
            clearRectTransparent(x0, y0, w, h);
            floating = { x: x0, y: y0, w, h, pixels, matchPixels };
            pushHistory();
            syncSelectActionsUi();
          }
        }
        drawing = false;
        moveGrab = null;
        redrawEditor();
        return;
      }
      if (selectMode === "move" && floating) {
        /* position kept until stamped */
      }
      selectMode = null;
      moveGrab = null;
      drawing = false;
      redrawEditor();
      return;
    }

    if (drawing && (tool === "pencil" || tool === "eraser" || tool === "line")) {
      commitOutlineStrokePreview();
      pushHistory();
      redrawEditor();
      refreshParticlePreviewCache();
    } else {
      resetOutlineStrokePreview();
    }
    drawing = false;
    lineStart = null;
    snapshot = null;
  }

  canvas.addEventListener("mousedown", pointerDown);
  canvas.addEventListener("mousemove", pointerMove);
  window.addEventListener("mouseup", pointerUp);
  canvas.addEventListener("touchstart", pointerDown, { passive: false });
  canvas.addEventListener("touchmove", pointerMove, { passive: false });
  window.addEventListener("touchend", pointerUp);

  toolSelect.onchange = () => {
    if (floating) commitFloating();
    marqueeStart = null;
    marqueeEnd = null;
    selectMode = null;
    tool = toolSelect.value as Tool;
    canvas.style.cursor =
      tool === "select"
        ? "default"
        : tool === "eyedropper"
          ? "cell"
          : tool === "text"
            ? "text"
            : "crosshair";
    syncSelectActionsUi();
    redrawEditor();
  };
  typeSelect.onchange = () => {
    cosmeticKind =
      typeSelect.value === "trail"
        ? "trail"
        : typeSelect.value === "banner"
          ? "banner"
          : typeSelect.value === "theme"
            ? "theme"
            : "cursor";
    if (cosmeticKind !== "cursor") newCursorCheck.checked = false;
    syncCosmeticKindUi();
  };
  newCursorCheck.onchange = () => {
    if (newCursorCheck.checked) {
      cosmeticKind = "cursor";
      typeSelect.value = "cursor";
      syncCosmeticKindUi();
      status.textContent =
        "New Cursor on — filled guide hidden; hitbox outline shown. Publish category locks to Cursor.";
    }
    redrawEditor();
  };
  trailModeSelect.onchange = () => {
    trailMode = trailModeSelect.value === "particle" ? "particle" : "multiColor";
    syncCosmeticKindUi();
  };
  addTrailColorBtn.onclick = () => {
    if (trailColors.length >= MAX_TRAIL_COLORS) return;
    trailColors.push(colorInput.value || "#ffffff");
    renderTrailColorInputs();
  };
  removeTrailColorBtn.onclick = () => {
    if (trailColors.length <= 1) return;
    trailColors.pop();
    renderTrailColorInputs();
  };
  zoomSelect.onchange = () => {
    zoom = Number(zoomSelect.value) || 8;
    redrawEditor();
  };
  refCheck.onchange = () => redrawEditor();
  symmetryCheck.onchange = () => redrawEditor();
  duplicateSelectionBtn.onclick = () => duplicateFloatingSelection();
  importBtn.onclick = () => {
    importFileInput.value = "";
    importFileInput.click();
  };
  importFileInput.onchange = () => {
    const file = importFileInput.files?.[0];
    importFileInput.value = "";
    if (!file) return;
    const err = validateImportImageFile(file);
    if (err) {
      status.textContent = err;
      return;
    }
    void loadImageFromFile(file)
      .then((img) => {
        openImportPreviewModal(img, file.name);
      })
      .catch((e) => {
        status.textContent = e instanceof Error ? e.message : "Could not read that image.";
      });
  };
  addLayerBtn.onclick = () => {
    if (floating) commitFloating();
    paintLayers.push(makePaintLayer());
    activeLayerIndex = paintLayers.length - 1;
    syncLayersUi();
    pushHistory();
    redrawEditor();
    status.textContent = `Added ${paintLayers[activeLayerIndex].name}.`;
  };
  deleteLayerBtn.onclick = () => {
    if (paintLayers.length <= 1) return;
    if (floating) commitFloating();
    const removed = paintLayers[activeLayerIndex];
    paintLayers.splice(activeLayerIndex, 1);
    activeLayerIndex = clamp(activeLayerIndex, 0, paintLayers.length - 1);
    syncLayersUi();
    pushHistory();
    redrawEditor();
    status.textContent = `Deleted ${removed.name}.`;
  };
  cursorGuideColor.oninput = () => {
    if (matchCursorCheck.checked) syncMatchCursorUi();
    redrawEditor();
  };
  canvasBgColor.oninput = () => redrawEditor();

  matchCursorCheck.onchange = () => {
    syncMatchCursorUi();
    status.textContent = matchCursorCheck.checked
      ? "Match cursor on — new strokes use the guide cursor color (shade for darker/lighter)."
      : "Match cursor off — using Color picker.";
  };
  matchShadeInput.oninput = () => {
    // Update next-stroke color only; do not rewrite pixels already on the canvas.
    syncMatchCursorUi();
  };

  glowCheck.onchange = () => {
    glowEnabled = glowCheck.checked;
    refreshFxStatusHint();
  };
  outlineCheck.onchange = () => {
    outlineEnabled = outlineCheck.checked;
    refreshFxStatusHint();
    status.textContent = outlineEnabled
      ? "Outline on — strokes and fills go to the Outline layer (not tinted in-game)."
      : "Outline off — painting on main art.";
  };
  animCheck.onchange = () => {
    animEnabled = animCheck.checked;
    syncAnimControls();
    refreshFxStatusHint();
    redrawEditor();
    refreshParticlePreviewCache();
  };
  frameCountInput.onchange = () => {
    setFrameCount(Number(frameCountInput.value) || 4);
    hideDuplicateFramePicker();
    refreshFxStatusHint();
  };
  prevFrameBtn.onclick = () => {
    if (!animEnabled) return;
    hideDuplicateFramePicker();
    currentFrameIndex = (currentFrameIndex - 1 + frameCount) % frameCount;
    updateFrameDisplay();
    refreshFxStatusHint();
    redrawEditor();
  };
  nextFrameBtn.onclick = () => {
    if (!animEnabled) return;
    hideDuplicateFramePicker();
    currentFrameIndex = (currentFrameIndex + 1) % frameCount;
    updateFrameDisplay();
    refreshFxStatusHint();
    redrawEditor();
  };
  playCheck.onchange = () => {
    playEnabled = playCheck.checked;
    if (playEnabled && animEnabled) startPlayLoop();
    else {
      stopPlayLoop();
      redrawEditor();
    }
  };
  duplicateFrameBtn.setAttribute("aria-expanded", "false");
  duplicateFrameBtn.setAttribute("aria-haspopup", "true");
  duplicateFrameBtn.onclick = () => openDuplicateFramePicker();

  function exportMainDataUrl() {
    if (floating) commitFloating();
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = artW;
    exportCanvas.height = artH;
    const ctx = exportCanvas.getContext("2d")!;
    compositePaintLayersOnto(ctx);
    return exportCanvas.toDataURL("image/png");
  }

  function exportAnimationFrames(): string[] | null {
    if (floating) commitFloating();
    ensureAnimFrameCanvases(frameCount);
    let anyInk = false;
    const urls: string[] = [];
    for (let i = 0; i < frameCount; i++) {
      const img = animCtxs[i].getImageData(0, 0, artW, artH);
      for (let j = 3; j < img.data.length; j += 4) {
        if (img.data[j] > 0) {
          anyInk = true;
          break;
        }
      }
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = artW;
      exportCanvas.height = artH;
      exportCanvas.getContext("2d")!.putImageData(img, 0, 0);
      const url = exportCanvas.toDataURL("image/png");
      if (url.length > 350_000) {
        throw new Error(
          `Animation frame ${i + 1} is too large. Keep drawings simple on the 128×128 canvas.`,
        );
      }
      urls.push(url);
    }
    return anyInk ? urls : null;
  }

  function exportDataUrl() {
    if (floating) commitFloating();
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = artW;
    exportCanvas.height = artH;
    const ctx = exportCanvas.getContext("2d")!;
    compositePaintLayersOnto(ctx);
    ctx.drawImage(outlineOff, 0, 0);
    const url = exportCanvas.toDataURL("image/png");
    // Guard against accidental huge payloads that can stall /api/shop for all users.
    if (url.length > 350_000) {
      throw new Error("Cosmetic image is too large. Keep drawings simple on the 128×128 canvas.");
    }
    return url;
  }

  function exportOutlineMaskDataUrl(): string | null {
    if (floating) commitFloating();
    const img = outlineCtx.getImageData(0, 0, artW, artH);
    let hasInk = false;
    for (let i = 3; i < img.data.length; i += 4) {
      if (img.data[i] > 0) {
        hasInk = true;
        break;
      }
    }
    if (!hasInk) return null;
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = artW;
    exportCanvas.height = artH;
    exportCanvas.getContext("2d")!.putImageData(img, 0, 0);
    return exportCanvas.toDataURL("image/png");
  }

  function maskHasInk(img: ImageData): boolean {
    for (let i = 3; i < img.data.length; i += 4) {
      if (img.data[i] > 0) return true;
    }
    return false;
  }

  function exportMatchCursorMaskDataUrl(): string | null {
    if (floating) commitFloating();
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = artW;
    exportCanvas.height = artH;
    const ctx = exportCanvas.getContext("2d")!;
    ctx.clearRect(0, 0, artW, artH);
    compositeMatchMasksOnto(ctx);
    const img = ctx.getImageData(0, 0, artW, artH);
    if (!maskHasInk(img)) return null;
    return exportCanvas.toDataURL("image/png");
  }

  function exportMatchCursorMaskFrames(): string[] | null {
    if (floating) commitFloating();
    if (!animEnabled || frameCount < 1) return null;
    ensureAnimFrameCanvases(frameCount);
    const urls: string[] = [];
    let anyInk = false;
    for (let i = 0; i < frameCount; i++) {
      const img = matchMaskAnimCtxs[i].getImageData(0, 0, artW, artH);
      if (maskHasInk(img)) anyInk = true;
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = artW;
      exportCanvas.height = artH;
      exportCanvas.getContext("2d")!.putImageData(img, 0, 0);
      urls.push(exportCanvas.toDataURL("image/png"));
    }
    return anyInk ? urls : null;
  }

  function loadAnimFramesFromUrls(
    frames: string[] | null | undefined,
    matchFrames: string[] | null | undefined,
    onDone: () => void,
  ) {
    clearAllAnimFrames();
    if (!frames?.length) {
      frameCount = 4;
      frameCountInput.value = "4";
      ensureAnimFrameCanvases(frameCount);
      onDone();
      return;
    }
    frameCount = clamp(frames.length, 1, MAX_ANIM_FRAMES);
    frameCountInput.value = String(frameCount);
    ensureAnimFrameCanvases(frameCount);
    currentFrameIndex = 0;
    let pending = frameCount;
    let failed = false;
    const finishOne = () => {
      pending -= 1;
      if (pending <= 0 && !failed) onDone();
    };
    frames.slice(0, frameCount).forEach((frameUrl, i) => {
      const fImg = new Image();
      fImg.onload = () => {
        animCtxs[i].clearRect(0, 0, artW, artH);
        animCtxs[i].drawImage(fImg, 0, 0, artW, artH);
        const mUrl = matchFrames?.[i];
        if (mUrl) {
          const mImg = new Image();
          mImg.onload = () => {
            matchMaskAnimCtxs[i].clearRect(0, 0, artW, artH);
            matchMaskAnimCtxs[i].drawImage(mImg, 0, 0, artW, artH);
            finishOne();
          };
          mImg.onerror = () => finishOne();
          mImg.src = mUrl.startsWith("data:") ? mUrl : `data:image/png;base64,${mUrl}`;
        } else {
          finishOne();
        }
      };
      fImg.onerror = () => {
        failed = true;
        onDone();
      };
      fImg.src = frameUrl.startsWith("data:") ? frameUrl : `data:image/png;base64,${frameUrl}`;
    });
  }

  function loadDataUrl(
    url: string,
    outlineUrl?: string | null,
    animFrames?: string[] | null,
    matchMaskUrl?: string | null,
    matchMaskFrames?: string[] | null,
  ) {
    const imageFit: "stretch" | "contain" = mode === "crateArt" ? "contain" : "stretch";
    const img = new Image();
    img.onload = () => {
      floating = null;
      resetPaintLayersToOne();
      syncLayersUi();
      const layer = activePaintLayerObj();
      drawImageOntoCanvas(layer.ctx, img, artW, artH, imageFit);
      outlineCtx.clearRect(0, 0, artW, artH);
      layer.matchCtx.clearRect(0, 0, artW, artH);
      const finishLoad = () => {
        updateFrameDisplay();
        syncAnimControls();
        pushHistory();
        redrawEditor();
      };
      const loadAnim = () => loadAnimFramesFromUrls(animFrames, matchMaskFrames, finishLoad);
      const loadMatchMask = (next: () => void) => {
        if (!matchMaskUrl) {
          next();
          return;
        }
        const mImg = new Image();
        mImg.onload = () => {
          activePaintLayerObj().matchCtx.drawImage(mImg, 0, 0, artW, artH);
          next();
        };
        mImg.onerror = () => next();
        mImg.src = matchMaskUrl.startsWith("data:")
          ? matchMaskUrl
          : `data:image/png;base64,${matchMaskUrl}`;
      };
      if (outlineUrl) {
        const oImg = new Image();
        oImg.onload = () => {
          outlineCtx.drawImage(oImg, 0, 0, artW, artH);
          const main = activePaintLayerObj().ctx.getImageData(0, 0, artW, artH);
          const mask = outlineCtx.getImageData(0, 0, artW, artH);
          for (let i = 3; i < mask.data.length; i += 4) {
            if (mask.data[i] > 0) {
              main.data[i - 3] = 0;
              main.data[i - 2] = 0;
              main.data[i - 1] = 0;
              main.data[i] = 0;
            }
          }
          activePaintLayerObj().ctx.putImageData(main, 0, 0);
          loadMatchMask(loadAnim);
        };
        oImg.onerror = () => loadMatchMask(loadAnim);
        oImg.src = outlineUrl.startsWith("data:")
          ? outlineUrl
          : `data:image/png;base64,${outlineUrl}`;
        return;
      }
      loadMatchMask(loadAnim);
    };
    img.src = url.startsWith("data:") ? url : `data:image/png;base64,${url}`;
  }

  function loadPublishedItem(item: ShopCreatorEditItem) {
    editingItem = item;
    status.textContent = item.id
      ? `Editing crate item "${item.name}" — publish to update.`
      : `Editing "${item.name}" — publish as new or replace an item.`;
    if (item.category === "trail") {
      cosmeticKind = "trail";
      trailMode = item.trailMode === "particle" ? "particle" : "multiColor";
      if (item.trailColors?.length) {
        trailColors = item.trailColors.slice(0, MAX_TRAIL_COLORS);
      }
    } else if (item.category === "banner") {
      cosmeticKind = "banner";
    } else if (item.category === "theme") {
      cosmeticKind = "theme";
      themeEditor?.setThemeData(item.themeData ?? null);
    } else {
      cosmeticKind = "cursor";
    }
    if (item.animationFrames?.length) {
      animEnabled = true;
      animCheck.checked = true;
    } else {
      animEnabled = false;
      animCheck.checked = false;
    }
    matchCursorCheck.checked = !!item.matchCursorColor || !!item.matchCursorMaskData;
    newCursorCheck.checked = !!item.newCursor && cosmeticKind === "cursor";
    const shade =
      typeof item.cursorColorShade === "number" && Number.isFinite(item.cursorColorShade)
        ? Math.round(Math.max(-1, Math.min(1, item.cursorColorShade)) * 100)
        : 0;
    matchShadeInput.value = String(shade);
    syncMatchCursorUi();
    syncAnimControls();
    syncCosmeticKindUi();
    if (cosmeticKind === "trail" && trailMode === "multiColor") {
      // Multi Color trails are config-only; keep canvas blank.
      resetPaintLayersToOne();
      syncLayersUi();
      outlineCtx.clearRect(0, 0, artW, artH);
      pushHistory();
      redrawEditor();
    } else if (cosmeticKind === "theme") {
      resetPaintLayersToOne();
      syncLayersUi();
      outlineCtx.clearRect(0, 0, artW, artH);
      pushHistory();
      redrawEditor();
    } else {
      loadDataUrl(
        item.imageData,
        item.outlineMaskData,
        item.animationFrames,
        item.matchCursorMaskData,
        item.matchCursorMaskFrames,
      );
    }
  }

  undoBtn.onclick = () => undo();
  redoBtn.onclick = () => redo();
  resetBtn.onclick = () => resetEditor();

  const onKeyUndoRedo = (e: KeyboardEvent) => {
    if (!root.isConnected) {
      window.removeEventListener("keydown", onKeyUndoRedo);
      return;
    }
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    if (key === "z" && !e.shiftKey) {
      e.preventDefault();
      undo();
    } else if (key === "y" || (key === "z" && e.shiftKey)) {
      e.preventDefault();
      redo();
    }
  };
  window.addEventListener("keydown", onKeyUndoRedo);

  saveBtn.onclick = async () => {
    status.textContent = "Saving…";
    try {
      const name = prompt("Project name (optional):") ?? "";
      const saveDraft = mode === "crateArt" ? saveAdminShopCrateDraft : saveAdminShopDraft;
      await saveDraft({
        name: name.trim() || undefined,
        data: {
          width: artW,
          height: artH,
          imageData: exportMainDataUrl(),
          outlineMaskData: exportOutlineMaskDataUrl(),
          animationFrames: exportAnimationFrames(),
          matchCursorMaskData: exportMatchCursorMaskDataUrl(),
          matchCursorMaskFrames: exportMatchCursorMaskFrames(),
          cosmeticKind,
          trailMode,
          trailColors: [...trailColors],
          themeData: themeEditor?.getThemeData() ?? null,
          newCursor: !!newCursorCheck.checked,
          activeLayerIndex,
          paintLayers: paintLayers.map((layer) => ({
            id: layer.id,
            name: layer.name,
            imageData: layer.canvas.toDataURL("image/png"),
            matchMaskData: layer.matchCanvas.toDataURL("image/png"),
          })),
        },
      });
      status.textContent = "Project saved.";
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Save failed.";
    }
  };

  loadBtn.onclick = async () => {
    status.textContent = "Loading drafts…";
    try {
      const items =
        mode === "crateArt" ? await fetchAdminShopCrateDrafts() : await fetchAdminShopDrafts();
      if (!items.length) {
        status.textContent = "No saved projects.";
        return;
      }
      const pick = await pickDraftModal(items);
      if (!pick) {
        status.textContent = "";
        return;
      }
      const row =
        mode === "crateArt" ? await fetchAdminShopCrateDraft(pick) : await fetchAdminShopDraft(pick);
      const data = row.data as {
        imageData?: string;
        outlineMaskData?: string | null;
        animationFrames?: string[] | null;
        matchCursorMaskData?: string | null;
        matchCursorMaskFrames?: string[] | null;
        cosmeticKind?: string;
        trailMode?: string;
        trailColors?: string[];
        themeData?: ThemeCosmeticData | null;
        newCursor?: boolean;
        activeLayerIndex?: number;
        paintLayers?: Array<{
          id?: number;
          name?: string;
          imageData?: string;
          matchMaskData?: string | null;
        }>;
      };
      if (supportsTrail) {
        cosmeticKind =
          data?.cosmeticKind === "trail"
            ? "trail"
            : data?.cosmeticKind === "banner"
              ? "banner"
              : data?.cosmeticKind === "theme"
                ? "theme"
                : "cursor";
        trailMode = data?.trailMode === "particle" ? "particle" : "multiColor";
        if (Array.isArray(data?.trailColors) && data.trailColors.length) {
          trailColors = data.trailColors.filter((c) => typeof c === "string").slice(0, MAX_TRAIL_COLORS);
          if (!trailColors.length) trailColors = ["#ff3366"];
        }
        if (cosmeticKind === "theme") {
          themeEditor?.setThemeData(data?.themeData ?? null);
        }
        newCursorCheck.checked = !!data?.newCursor && cosmeticKind === "cursor";
        syncCosmeticKindUi();
      }
      const finishDraftLoad = () => {
        status.textContent = `Loaded ${row.name || pick}.`;
      };
      if (
        Array.isArray(data?.paintLayers) &&
        data.paintLayers.length > 0 &&
        !(cosmeticKind === "trail" && trailMode === "multiColor")
      ) {
        if (data.animationFrames?.length) {
          animEnabled = true;
          animCheck.checked = true;
        } else {
          animEnabled = false;
          animCheck.checked = false;
        }
        syncAnimControls();
        const layerRows = data.paintLayers;
        let pending = layerRows.length;
        const built: PaintLayer[] = [];
        const doneOne = () => {
          pending -= 1;
          if (pending > 0) return;
          paintLayers = built.length ? built : [makePaintLayer("Layer 1")];
          nextPaintLayerId = Math.max(0, ...paintLayers.map((l) => l.id)) + 1;
          activeLayerIndex = clamp(data.activeLayerIndex ?? 0, 0, paintLayers.length - 1);
          syncLayersUi();
          if (data.outlineMaskData) {
            const oImg = new Image();
            oImg.onload = () => {
              outlineCtx.clearRect(0, 0, artW, artH);
              outlineCtx.drawImage(oImg, 0, 0, artW, artH);
              loadAnimFramesFromUrls(data.animationFrames, data.matchCursorMaskFrames, () => {
                pushHistory();
                redrawEditor();
                finishDraftLoad();
              });
            };
            oImg.onerror = () => {
              loadAnimFramesFromUrls(data.animationFrames, data.matchCursorMaskFrames, () => {
                pushHistory();
                redrawEditor();
                finishDraftLoad();
              });
            };
            oImg.src = data.outlineMaskData.startsWith("data:")
              ? data.outlineMaskData
              : `data:image/png;base64,${data.outlineMaskData}`;
          } else {
            outlineCtx.clearRect(0, 0, artW, artH);
            loadAnimFramesFromUrls(data.animationFrames, data.matchCursorMaskFrames, () => {
              pushHistory();
              redrawEditor();
              finishDraftLoad();
            });
          }
        };
        layerRows.forEach((rowLayer, idx) => {
          const layer = makePaintLayer(rowLayer.name || `Layer ${idx + 1}`);
          if (typeof rowLayer.id === "number") layer.id = rowLayer.id;
          built[idx] = layer;
          if (!rowLayer.imageData) {
            doneOne();
            return;
          }
          const limg = new Image();
          limg.onload = () => {
            layer.ctx.clearRect(0, 0, artW, artH);
            layer.ctx.drawImage(limg, 0, 0, artW, artH);
            if (rowLayer.matchMaskData) {
              const mImg = new Image();
              mImg.onload = () => {
                layer.matchCtx.clearRect(0, 0, artW, artH);
                layer.matchCtx.drawImage(mImg, 0, 0, artW, artH);
                doneOne();
              };
              mImg.onerror = () => doneOne();
              mImg.src = rowLayer.matchMaskData.startsWith("data:")
                ? rowLayer.matchMaskData
                : `data:image/png;base64,${rowLayer.matchMaskData}`;
            } else doneOne();
          };
          limg.onerror = () => doneOne();
          limg.src = rowLayer.imageData.startsWith("data:")
            ? rowLayer.imageData
            : `data:image/png;base64,${rowLayer.imageData}`;
        });
      } else if (data?.imageData && !(cosmeticKind === "trail" && trailMode === "multiColor")) {
        if (data.animationFrames?.length) {
          animEnabled = true;
          animCheck.checked = true;
        } else {
          animEnabled = false;
          animCheck.checked = false;
        }
        syncAnimControls();
        loadDataUrl(
          data.imageData,
          data.outlineMaskData,
          data.animationFrames,
          data.matchCursorMaskData,
          data.matchCursorMaskFrames,
        );
        finishDraftLoad();
      } else {
        finishDraftLoad();
      }
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Load failed.";
    }
  };

  publishBtn.onclick = () => {
    if (mode === "crateArt") {
      void saveCrateArt();
      return;
    }
    void openPublishModal();
  };

  async function saveCrateArt() {
    if (!editingCrateId) {
      status.textContent = "Open a crate from Admin → Crates → Edit art.";
      return;
    }
    status.textContent = "Saving crate art…";
    try {
      await patchAdminShopCrate({
        id: editingCrateId,
        imageData: exportDataUrl(),
      });
      status.textContent = "Crate art saved.";
      opts?.onCrateArtSaved?.();
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Save failed.";
    }
  }

  function buildTrailOrCursorArtFields(categorySelect: HTMLSelectElement, customColor: boolean, repurchase: boolean) {
    if (cosmeticKind === "theme") {
      const themeData = themeEditor?.getThemeData() ?? null;
      if (!themeData) {
        throw new Error(
          "Enable at least one Theme channel (Background, Balls, Buttons, Text, or Particles) and configure it.",
        );
      }
      const imageData = themeEditor?.getPreviewDataUrl() || "";
      if (!imageData) throw new Error("Theme preview failed.");
      return {
        imageData,
        outlineMaskData: null as string | null,
        animationFrames: null as string[] | null,
        category: "theme" as CosmeticCategory,
        trailMode: null as TrailMode | null,
        trailColors: null as string[] | null,
        themeData,
        allowCustomColor: false,
        allowRepurchase: false,
        matchCursorColor: false,
        cursorColorShade: 0,
        matchCursorMaskData: null as string | null,
        matchCursorMaskFrames: null as string[] | null,
        newCursor: false,
      };
    }
    if (cosmeticKind === "trail") {
      if (trailMode === "multiColor") {
        if (!trailColors.length) throw new Error("Add at least one trail color.");
        return {
          imageData: buildMultiColorTrailPreviewDataUrl(trailColors),
          outlineMaskData: null as string | null,
          animationFrames: null as string[] | null,
          category: "trail" as CosmeticCategory,
          trailMode: "multiColor" as TrailMode,
          trailColors: [...trailColors],
          themeData: null as ThemeCosmeticData | null,
          allowCustomColor: false,
          allowRepurchase: false,
          matchCursorColor: false,
          cursorColorShade: 0,
          matchCursorMaskData: null as string | null,
          matchCursorMaskFrames: null as string[] | null,
          newCursor: false,
        };
      }
      return {
        imageData: exportDataUrl(),
        outlineMaskData: exportOutlineMaskDataUrl(),
        animationFrames: exportAnimationFrames(),
        category: "trail" as CosmeticCategory,
        trailMode: "particle" as TrailMode,
        trailColors: null as string[] | null,
        themeData: null as ThemeCosmeticData | null,
        allowCustomColor: false,
        allowRepurchase: false,
        matchCursorColor: false,
        cursorColorShade: 0,
        matchCursorMaskData: null as string | null,
        matchCursorMaskFrames: null as string[] | null,
        newCursor: false,
      };
    }
    if (cosmeticKind === "banner") {
      return {
        imageData: exportDataUrl(),
        outlineMaskData: null as string | null,
        animationFrames: exportAnimationFrames(),
        category: "banner" as CosmeticCategory,
        trailMode: null as TrailMode | null,
        trailColors: null as string[] | null,
        themeData: null as ThemeCosmeticData | null,
        allowCustomColor: false,
        allowRepurchase: false,
        matchCursorColor: false,
        cursorColorShade: 0,
        matchCursorMaskData: null as string | null,
        matchCursorMaskFrames: null as string[] | null,
        newCursor: false,
      };
    }
    const matchMask = exportMatchCursorMaskDataUrl();
    const matchMaskFrames = exportMatchCursorMaskFrames();
    const isNewCursor = !!newCursorCheck.checked;
    return {
      imageData: exportDataUrl(),
      outlineMaskData: exportOutlineMaskDataUrl(),
      animationFrames: exportAnimationFrames(),
      category: (isNewCursor ? "cursor_color" : categorySelect.value) as CosmeticCategory,
      trailMode: null as TrailMode | null,
      trailColors: null as string[] | null,
      themeData: null as ThemeCosmeticData | null,
      allowCustomColor: customColor,
      allowRepurchase: repurchase,
      matchCursorColor: !!(matchMask || matchMaskFrames),
      cursorColorShade: 0,
      matchCursorMaskData: matchMask,
      matchCursorMaskFrames: matchMaskFrames,
      newCursor: isNewCursor,
    };
  }

  async function openCrateItemPublishModal() {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>Publish crate item</h2>`;

    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent =
      "This item is only obtainable from crates — it will not appear in Shop → Items. Choose Don't Replace to publish as new, or Replace to overwrite an existing crate item.";
    box.appendChild(hint);

    const nameInput = document.createElement("input");
    nameInput.className = "formInput";
    nameInput.placeholder = "Item name";
    if (editingItem) nameInput.value = editingItem.name;

    const replaceModeSelect = document.createElement("select");
    replaceModeSelect.className = "formInput";
    replaceModeSelect.setAttribute("aria-label", "Replace mode");
    (
      [
        ["none", "Don't Replace"],
        ["replace", "Replace"],
      ] as const
    ).forEach(([value, label]) => {
      const o = document.createElement("option");
      o.value = value;
      o.textContent = label;
      replaceModeSelect.appendChild(o);
    });
    replaceModeSelect.value =
      editingItem && opts?.permissions?.overwrite !== false ? "replace" : "none";
    if (opts?.permissions?.overwrite === false) {
      replaceModeSelect.querySelector('option[value="replace"]')?.setAttribute("disabled", "true");
    }
    if (opts?.permissions?.publish === false) {
      replaceModeSelect.querySelector('option[value="none"]')?.setAttribute("disabled", "true");
      if (opts?.permissions?.overwrite !== false) replaceModeSelect.value = "replace";
    }

    const replaceItemSelect = document.createElement("select");
    replaceItemSelect.className = "formInput";
    replaceItemSelect.setAttribute("aria-label", "Crate item to replace");
    const replaceEmpty = document.createElement("option");
    replaceEmpty.value = "";
    replaceEmpty.textContent = "Select crate item…";
    replaceItemSelect.appendChild(replaceEmpty);

    let crateItems: AdminShopCosmeticRow[] = [];
    try {
      const all = await fetchAdminShopCosmetics();
      crateItems = all.filter((item) => !!item.crateOnly);
      crateItems.forEach((item) => {
        const o = document.createElement("option");
        o.value = item.id;
        o.textContent = item.name;
        replaceItemSelect.appendChild(o);
      });
      if (editingItem && crateItems.some((item) => item.id === editingItem!.id)) {
        replaceItemSelect.value = editingItem.id;
      }
    } catch {
      /* ignore */
    }

    const categorySelect = document.createElement("select");
    categorySelect.className = "formInput";
    (
      [
        ["hat", "Hat"],
        ["face", "Face"],
        ["cursor", "Body"],
        ["cursor_color", "Cursor"],
      ] as Array<[CosmeticCategory, string]>
    ).forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      categorySelect.appendChild(o);
    });
    if (editingItem?.category) categorySelect.value = editingItem.category;
    if (newCursorCheck.checked) {
      categorySelect.value = "cursor_color";
      categorySelect.disabled = true;
      categorySelect.title = "Locked to Cursor while New Cursor is enabled.";
    }

    const customColorCheck = document.createElement("input");
    customColorCheck.type = "checkbox";
    customColorCheck.id = "cratePublishCustomColor";
    customColorCheck.checked = !!editingItem?.allowCustomColor;
    const customColorLabel = document.createElement("label");
    customColorLabel.htmlFor = customColorCheck.id;
    customColorLabel.className = "adminShopCheckLabel";
    customColorLabel.appendChild(customColorCheck);
    customColorLabel.appendChild(document.createTextNode(" Allow custom color"));

    const repurchaseCheck = document.createElement("input");
    repurchaseCheck.type = "checkbox";
    repurchaseCheck.id = "cratePublishRepurchase";
    repurchaseCheck.checked = !!editingItem?.allowRepurchase;
    const repurchaseLabel = document.createElement("label");
    repurchaseLabel.htmlFor = repurchaseCheck.id;
    repurchaseLabel.className = "adminShopCheckLabel";
    repurchaseLabel.appendChild(repurchaseCheck);
    repurchaseLabel.appendChild(document.createTextNode(" Allow re-tint"));

    const crateSelect = document.createElement("select");
    crateSelect.className = "formInput";
    const newCrateOpt = document.createElement("option");
    newCrateOpt.value = "__new__";
    newCrateOpt.textContent = "Create new crate with this item";
    crateSelect.appendChild(newCrateOpt);

    let crates: AdminShopCrateRow[] = [];
    try {
      crates = await fetchAdminShopCrates();
      crates.forEach((crate) => {
        const o = document.createElement("option");
        o.value = crate.id;
        o.textContent = `${crate.name} (${crate.isPublished ? "Published" : "Draft"})`;
        crateSelect.appendChild(o);
      });
    } catch {
      /* ignore */
    }

    const newCrateNameInput = document.createElement("input");
    newCrateNameInput.className = "formInput";
    newCrateNameInput.placeholder = "New crate name";
    newCrateNameInput.value = "Untitled Crate";

    const err = document.createElement("p");
    err.className = "accessHint";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = "btn green";
    okBtn.textContent = "Publish";

    const row = (label: string, el: HTMLElement) => {
      const fr = document.createElement("div");
      fr.className = "formRow";
      const sp = document.createElement("span");
      sp.className = "formLabel";
      sp.textContent = label;
      fr.appendChild(sp);
      fr.appendChild(el);
      box.appendChild(fr);
      return fr;
    };

    row("Name", nameInput);
    row("Mode", replaceModeSelect);
    const replaceItemRow = row("Replace item", replaceItemSelect);
    const categoryRow = row("Category", categorySelect);
    box.appendChild(customColorLabel);
    box.appendChild(repurchaseLabel);
    const crateRow = row("Add to crate", crateSelect);
    const newCrateRow = row("New crate name", newCrateNameInput);
    box.appendChild(err);

    if (cosmeticKind === "trail" || cosmeticKind === "banner" || cosmeticKind === "theme") {
      categoryRow.classList.add("hidden");
      customColorLabel.classList.add("hidden");
      repurchaseLabel.classList.add("hidden");
    } else if (newCursorCheck.checked) {
      const lockHint = document.createElement("p");
      lockHint.className = "accessHint";
      lockHint.textContent = "New Cursor is on — category is locked to Cursor.";
      categoryRow.after(lockHint);
    }

    const syncPublishFields = () => {
      const replacing = replaceModeSelect.value === "replace";
      replaceItemRow.classList.toggle("hidden", !replacing);
      crateRow.classList.toggle("hidden", replacing);
      newCrateRow.classList.toggle("hidden", replacing || crateSelect.value !== "__new__");
      okBtn.textContent = replacing ? "Replace" : "Publish";
    };
    replaceModeSelect.onchange = syncPublishFields;
    crateSelect.onchange = syncPublishFields;
    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    actions.appendChild(cancelBtn);
    actions.appendChild(okBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);
    syncPublishFields();

    cancelBtn.onclick = () => modal.remove();
    okBtn.onclick = async () => {
      const name = nameInput.value.trim();
      if (!name) {
        err.textContent = "Name required.";
        return;
      }
      const replacing = replaceModeSelect.value === "replace";
      const replaceId = replacing ? replaceItemSelect.value || null : null;
      if (replacing) {
        if (!opts?.permissions?.overwrite) {
          err.textContent = "Missing Overwrite Crates Items permission.";
          return;
        }
        if (!replaceId) {
          err.textContent = "Pick a crate item to replace.";
          return;
        }
      } else if (!opts?.permissions?.publish) {
        err.textContent = "Missing Publish Crates Items permission.";
        return;
      }
      if (!replacing && crateSelect.value === "__new__") {
        const crateName = newCrateNameInput.value.trim();
        if (!crateName) {
          err.textContent = "New crate name required.";
          return;
        }
      }
      err.textContent = replacing ? "Replacing…" : "Publishing…";
      try {
        const artPayload = {
          name,
          ...buildTrailOrCursorArtFields(
            categorySelect,
            customColorCheck.checked,
            repurchaseCheck.checked,
          ),
        };

        if (replaceId) {
          await patchAdminShopCosmetic({ id: replaceId, ...artPayload });
          modal.remove();
          status.textContent = `"${name}" replaced.`;
          editingItem = null;
          return;
        }

        const { id: cosmeticId } = await publishAdminShopCosmetic({
          ...artPayload,
          unlockType: "free",
          crateOnly: true,
        });
        if (!cosmeticId) throw new Error("publish_failed");

        if (crateSelect.value === "__new__") {
          const crateName = newCrateNameInput.value.trim() || "Untitled Crate";
          const { id: crateId } = await publishAdminShopCrate({ name: crateName, imageData: "" });
          if (!crateId) throw new Error("crate_create_failed");
          await patchAdminShopCrate({
            id: crateId,
            contents: [{ cosmeticId, probabilityPercent: 100 }],
          });
          modal.remove();
          status.textContent = `"${name}" published and added to new crate "${crateName}".`;
        } else {
          const crate = crates.find((entry) => entry.id === crateSelect.value);
          if (!crate) throw new Error("crate_not_found");
          const contentIds = [
            ...new Set([...crate.contents.map((entry) => entry.cosmeticId), cosmeticId]),
          ];
          await patchAdminShopCrate({
            id: crate.id,
            contents: contentIds.map((id) => ({ cosmeticId: id, probabilityPercent: 0 })),
          });
          modal.remove();
          status.textContent = `"${name}" published and added to crate "${crate.name}".`;
        }
        editingItem = null;
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "Publish failed.";
      }
    };
  }

  async function openCampaignItemPublishModal() {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>Publish Campaign exclusive</h2>`;

    const nameInput = document.createElement("input");
    nameInput.className = "formInput";
    nameInput.placeholder = "Item name";

    const categorySelect = document.createElement("select");
    categorySelect.className = "formInput";
    (
      [
        ["hat", "Hat"],
        ["face", "Face"],
        ["cursor", "Body"],
        ["cursor_color", "Cursor"],
        ["trail", "Trail"],
        ["banner", "Banner"],
      ] as Array<[CosmeticCategory, string]>
    ).forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      categorySelect.appendChild(o);
    });
    if (cosmeticKind === "trail") categorySelect.value = "trail";
    else if (cosmeticKind === "banner") categorySelect.value = "banner";
    else if (cosmeticKind === "theme") categorySelect.value = "theme";
    else if (newCursorCheck.checked) {
      categorySelect.value = "cursor_color";
      categorySelect.disabled = true;
    } else categorySelect.value = "cursor";

    const err = document.createElement("p");
    err.className = "accessHint";
    const actions = document.createElement("div");
    actions.className = "formRow";
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = "btn green";
    okBtn.textContent = "Publish";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn";
    cancel.textContent = "Cancel";
    cancel.onclick = () => modal.remove();
    actions.append(okBtn, cancel);

    const row = (label: string, el: HTMLElement) => {
      const r = document.createElement("div");
      r.className = "formRow";
      r.append(
        Object.assign(document.createElement("span"), { className: "formLabel", textContent: label }),
        el,
      );
      return r;
    };

    box.append(
      createModalCloseButton(() => modal.remove()),
      row("Name", nameInput),
      row("Category", categorySelect),
      err,
      actions,
    );
    modal.appendChild(box);
    document.body.appendChild(modal);
    nameInput.focus();

    okBtn.onclick = async () => {
      const name = nameInput.value.trim();
      if (!name) {
        err.textContent = "Name is required.";
        return;
      }
      err.textContent = "Publishing…";
      try {
        const art = buildTrailOrCursorArtFields(categorySelect, false, false);
        const created = await createAdminCampaignCosmetic({
          ...art,
          name,
          unlockCampaignNodeId: opts?.unlockCampaignNodeId ?? null,
        });
        modal.remove();
        status.textContent = `Published exclusive “${created.name}”.`;
        opts?.onCampaignItemPublished?.(created);
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "Publish failed.";
      }
    };
  }

  async function openTaskItemPublishModal() {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>Publish Task exclusive</h2>`;

    const nameInput = document.createElement("input");
    nameInput.className = "formInput";
    nameInput.placeholder = "Item name";

    const categorySelect = document.createElement("select");
    categorySelect.className = "formInput";
    (
      [
        ["hat", "Hat"],
        ["face", "Face"],
        ["cursor", "Body"],
        ["cursor_color", "Cursor"],
        ["trail", "Trail"],
        ["banner", "Banner"],
        ["theme", "Theme"],
      ] as Array<[CosmeticCategory, string]>
    ).forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      categorySelect.appendChild(o);
    });
    if (cosmeticKind === "trail") categorySelect.value = "trail";
    else if (cosmeticKind === "banner") categorySelect.value = "banner";
    else if (cosmeticKind === "theme") categorySelect.value = "theme";
    else if (newCursorCheck.checked) {
      categorySelect.value = "cursor_color";
      categorySelect.disabled = true;
    } else categorySelect.value = "cursor";

    const err = document.createElement("p");
    err.className = "accessHint";
    const actions = document.createElement("div");
    actions.className = "formRow";
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = "btn green";
    okBtn.textContent = "Publish";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn";
    cancel.textContent = "Cancel";
    cancel.onclick = () => modal.remove();
    actions.append(okBtn, cancel);

    const row = (label: string, el: HTMLElement) => {
      const r = document.createElement("div");
      r.className = "formRow";
      r.append(
        Object.assign(document.createElement("span"), { className: "formLabel", textContent: label }),
        el,
      );
      return r;
    };

    box.append(
      createModalCloseButton(() => modal.remove()),
      row("Name", nameInput),
      row("Category", categorySelect),
      err,
      actions,
    );
    modal.appendChild(box);
    document.body.appendChild(modal);
    nameInput.focus();

    okBtn.onclick = async () => {
      const name = nameInput.value.trim();
      if (!name) {
        err.textContent = "Name is required.";
        return;
      }
      err.textContent = "Publishing…";
      try {
        const art = buildTrailOrCursorArtFields(categorySelect, false, false);
        const created = await createAdminTaskCosmetic({
          ...art,
          name,
          unlockTaskId: opts?.unlockTaskId ?? null,
        });
        modal.remove();
        status.textContent = `Published exclusive “${created.name}”.`;
        opts?.onTaskItemPublished?.(created);
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "Publish failed.";
      }
    };
  }

  async function openBattlePassItemPublishModal() {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>${editingItem ? "Update Battle Pass exclusive" : "Battle Pass exclusive"}</h2>`;

    const nameInput = document.createElement("input");
    nameInput.className = "formInput";
    nameInput.placeholder = "Item name";
    if (editingItem?.name) nameInput.value = editingItem.name;

    const categorySelect = document.createElement("select");
    categorySelect.className = "formInput";
    (
      [
        ["hat", "Hat"],
        ["face", "Face"],
        ["cursor", "Body"],
        ["cursor_color", "Cursor"],
        ["trail", "Trail"],
        ["banner", "Banner"],
        ["theme", "Theme"],
      ] as Array<[CosmeticCategory, string]>
    ).forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      categorySelect.appendChild(o);
    });
    if (editingItem?.category) categorySelect.value = editingItem.category;
    else if (cosmeticKind === "trail") categorySelect.value = "trail";
    else if (cosmeticKind === "banner") categorySelect.value = "banner";
    else if (cosmeticKind === "theme") categorySelect.value = "theme";
    else if (newCursorCheck.checked) {
      categorySelect.value = "cursor_color";
      categorySelect.disabled = true;
    } else categorySelect.value = "cursor";

    const err = document.createElement("p");
    err.className = "accessHint";
    const actions = document.createElement("div");
    actions.className = "formRow";
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = "btn green";
    okBtn.textContent = "Done";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn";
    cancel.textContent = "Cancel";
    cancel.onclick = () => modal.remove();
    actions.append(okBtn, cancel);

    const row = (label: string, el: HTMLElement) => {
      const r = document.createElement("div");
      r.className = "formRow";
      r.append(
        Object.assign(document.createElement("span"), { className: "formLabel", textContent: label }),
        el,
      );
      return r;
    };

    box.append(
      createModalCloseButton(() => modal.remove()),
      row("Name", nameInput),
      row("Category", categorySelect),
      err,
      actions,
    );
    modal.appendChild(box);
    document.body.appendChild(modal);
    nameInput.focus();

    okBtn.onclick = async () => {
      const name = nameInput.value.trim();
      if (!name) {
        err.textContent = "Name is required.";
        return;
      }
      err.textContent = editingItem ? "Saving…" : "Publishing…";
      try {
        const art = buildTrailOrCursorArtFields(categorySelect, false, false);
        if (editingItem?.id) {
          const updated = await patchAdminBattlePassCosmetic({
            id: editingItem.id,
            ...art,
            name,
          });
          modal.remove();
          status.textContent = `Updated exclusive “${updated.cosmetic.name}”.`;
          opts?.onBattlePassItemPublished?.(updated.cosmetic);
        } else {
          const created = await createAdminBattlePassCosmetic({
            ...art,
            name,
            unlockBattlePassLevelId: opts?.unlockBattlePassLevelId ?? null,
          });
          modal.remove();
          status.textContent = `Published exclusive “${created.cosmetic.name}”.`;
          opts?.onBattlePassItemPublished?.(created.cosmetic);
        }
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "Save failed.";
      }
    };
  }

  async function openMarathonItemPublishModal() {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>Publish Marathon exclusive</h2>`;

    const nameInput = document.createElement("input");
    nameInput.className = "formInput";
    nameInput.placeholder = "Item name";

    const categorySelect = document.createElement("select");
    categorySelect.className = "formInput";
    (
      [
        ["hat", "Hat"],
        ["face", "Face"],
        ["cursor", "Body"],
        ["cursor_color", "Cursor"],
        ["trail", "Trail"],
        ["banner", "Banner"],
        ["theme", "Theme"],
      ] as Array<[CosmeticCategory, string]>
    ).forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      categorySelect.appendChild(o);
    });
    if (cosmeticKind === "trail") categorySelect.value = "trail";
    else if (cosmeticKind === "banner") categorySelect.value = "banner";
    else if (cosmeticKind === "theme") categorySelect.value = "theme";
    else if (newCursorCheck.checked) {
      categorySelect.value = "cursor_color";
      categorySelect.disabled = true;
    } else categorySelect.value = "cursor";

    const err = document.createElement("p");
    err.className = "accessHint";
    const actions = document.createElement("div");
    actions.className = "formRow";
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = "btn green";
    okBtn.textContent = "Publish";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn";
    cancel.textContent = "Cancel";
    cancel.onclick = () => modal.remove();
    actions.append(okBtn, cancel);

    const row = (label: string, el: HTMLElement) => {
      const r = document.createElement("div");
      r.className = "formRow";
      r.append(
        Object.assign(document.createElement("span"), { className: "formLabel", textContent: label }),
        el,
      );
      return r;
    };

    box.append(
      createModalCloseButton(() => modal.remove()),
      row("Name", nameInput),
      row("Category", categorySelect),
      err,
      actions,
    );
    modal.appendChild(box);
    document.body.appendChild(modal);
    nameInput.focus();

    okBtn.onclick = async () => {
      const name = nameInput.value.trim();
      if (!name) {
        err.textContent = "Name is required.";
        return;
      }
      err.textContent = "Publishing…";
      try {
        const art = buildTrailOrCursorArtFields(categorySelect, false, false);
        const created = await createAdminMarathonCosmetic({
          ...art,
          name,
          unlockMarathonSegmentId: opts?.unlockMarathonSegmentId ?? null,
        });
        modal.remove();
        status.textContent = `Published exclusive “${created.name}”.`;
        opts?.onMarathonItemPublished?.(created);
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "Publish failed.";
      }
    };
  }

  async function openPublishModal() {
    if (mode === "crateArt") return;
    if (mode === "crateItem") {
      await openCrateItemPublishModal();
      return;
    }
    if (mode === "campaignItem") {
      openCampaignItemPublishModal();
      return;
    }
    if (mode === "taskItem") {
      openTaskItemPublishModal();
      return;
    }
    if (mode === "marathonItem") {
      openMarathonItemPublishModal();
      return;
    }
    if (mode === "battlePassItem") {
      openBattlePassItemPublishModal();
      return;
    }

    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>${editingItem ? "Publish / replace cosmetic" : "Publish cosmetic"}</h2>`;

    const nameInput = document.createElement("input");
    nameInput.className = "formInput";
    nameInput.placeholder = "Cosmetic name";
    if (editingItem) nameInput.value = editingItem.name;

    const destSelect = document.createElement("select");
    destSelect.className = "formInput";
    const destNew = document.createElement("option");
    destNew.value = "new";
    destNew.textContent = "Publish as new item";
    destSelect.appendChild(destNew);
    const destSend = document.createElement("option");
    destSend.value = "send";
    destSend.textContent = "Send to";
    destSelect.appendChild(destSend);
    if (editingItem && opts?.permissions?.overwrite !== false) {
      const destReplaceSelf = document.createElement("option");
      destReplaceSelf.value = "replace-self";
      destReplaceSelf.textContent = `Replace "${editingItem.name}"`;
      destSelect.appendChild(destReplaceSelf);
      destSelect.value = editingItem.sentOnly ? "send" : "replace-self";
    }
    if (opts?.permissions?.overwrite !== false) {
      const destReplaceOther = document.createElement("option");
      destReplaceOther.value = "replace-other";
      destReplaceOther.textContent = "Replace another item…";
      destSelect.appendChild(destReplaceOther);
    }
    if (opts?.permissions?.publish === false && (destSelect.value === "new" || destSelect.value === "send")) {
      const firstReplace = destSelect.querySelector('option[value^="replace"]') as HTMLOptionElement | null;
      if (firstReplace) destSelect.value = firstReplace.value;
      destNew.disabled = true;
      destSend.disabled = true;
    }

    const replaceOtherSelect = document.createElement("select");
    replaceOtherSelect.className = "formInput hidden";
    replaceOtherSelect.setAttribute("aria-label", "Item to replace");

    let catalog: AdminShopCosmeticRow[] = [];
    try {
      catalog = await fetchAdminShopCosmetics();
      catalog.forEach((it) => {
        if (editingItem && it.id === editingItem.id) return;
        if (it.crateOnly || it.sentOnly) return;
        const o = document.createElement("option");
        o.value = it.id;
        o.textContent = it.name;
        replaceOtherSelect.appendChild(o);
      });
    } catch {
      /* ignore */
    }

    const sendPanel = document.createElement("div");
    sendPanel.className = "adminShopSendPanel hidden";
    const sendHint = document.createElement("p");
    sendHint.className = "accessHint";
    sendHint.textContent =
      editingItem?.sentOnly
        ? "Update this sent item and grant it to the users below (not listed in Shop)."
        : "Not listed in Shop. Only the users you add will receive this item in their inventory.";
    const sendSearch = document.createElement("input");
    sendSearch.className = "formInput";
    sendSearch.type = "search";
    sendSearch.placeholder = "Search users (min 2 characters)";
    sendSearch.setAttribute("aria-label", "Search users to send to");
    const sendResults = document.createElement("div");
    sendResults.className = "adminShopSendResults";
    const sendChips = document.createElement("div");
    sendChips.className = "adminShopSendChips";
    const recipients: Array<{ id: string; displayName: string | null }> = [];
    const renderChips = () => {
      sendChips.replaceChildren();
      if (!recipients.length) {
        const empty = document.createElement("span");
        empty.className = "accessHint";
        empty.textContent = "No recipients yet.";
        sendChips.appendChild(empty);
        return;
      }
      for (const u of recipients) {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "btn adminShopSendChip";
        chip.textContent = `${u.displayName || u.id} ×`;
        chip.title = "Remove";
        chip.onclick = () => {
          const idx = recipients.findIndex((r) => r.id === u.id);
          if (idx >= 0) recipients.splice(idx, 1);
          renderChips();
        };
        sendChips.appendChild(chip);
      }
    };
    renderChips();
    let searchTimer = 0;
    const runSearch = async () => {
      const q = sendSearch.value.trim();
      sendResults.replaceChildren();
      if (q.length < 2) return;
      try {
        const users = await searchUsersByName(q);
        for (const u of users) {
          if (recipients.some((r) => r.id === u.id)) continue;
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "btn";
          btn.textContent = u.displayName || u.id;
          btn.onclick = () => {
            recipients.push(u);
            renderChips();
            void runSearch();
          };
          sendResults.appendChild(btn);
        }
        if (!sendResults.childElementCount) {
          const none = document.createElement("span");
          none.className = "accessHint";
          none.textContent = "No users found.";
          sendResults.appendChild(none);
        }
      } catch {
        const fail = document.createElement("span");
        fail.className = "accessHint";
        fail.textContent = "Search failed.";
        sendResults.appendChild(fail);
      }
    };
    sendSearch.oninput = () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => void runSearch(), 250);
    };
    sendPanel.appendChild(sendHint);
    sendPanel.appendChild(sendSearch);
    sendPanel.appendChild(sendResults);
    sendPanel.appendChild(sendChips);

    const shopFieldsWrap = document.createElement("div");
    shopFieldsWrap.className = "adminShopPublishShopFields";

    const syncDestFields = () => {
      const dest = destSelect.value;
      replaceOtherSelect.classList.toggle("hidden", dest !== "replace-other");
      sendPanel.classList.toggle("hidden", dest !== "send");
      shopFieldsWrap.classList.toggle("hidden", dest === "send");
      okBtn.textContent = dest === "send" ? "Send" : "Publish";
    };
    destSelect.onchange = syncDestFields;

    const categorySelect = document.createElement("select");
    categorySelect.className = "formInput";
    (
      [
        ["hat", "Hat"],
        ["face", "Face"],
        ["cursor", "Body"],
        ["cursor_color", "Cursor"],
      ] as Array<[CosmeticCategory, string]>
    ).forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      categorySelect.appendChild(o);
    });
    categorySelect.value = editingItem?.category || "cursor";
    if (newCursorCheck.checked) {
      categorySelect.value = "cursor_color";
      categorySelect.disabled = true;
      categorySelect.title = "Locked to Cursor while New Cursor is enabled.";
    }

    const customColorCheck = document.createElement("input");
    customColorCheck.type = "checkbox";
    customColorCheck.id = "shopPublishCustomColor";
    customColorCheck.checked = !!editingItem?.allowCustomColor;
    const customColorLabel = document.createElement("label");
    customColorLabel.htmlFor = customColorCheck.id;
    customColorLabel.className = "adminShopCheckLabel";
    customColorLabel.appendChild(customColorCheck);
    customColorLabel.appendChild(document.createTextNode(" Allow custom color at purchase"));

    const repurchaseCheck = document.createElement("input");
    repurchaseCheck.type = "checkbox";
    repurchaseCheck.id = "shopPublishRepurchase";
    repurchaseCheck.checked = !!editingItem?.allowRepurchase;
    const repurchaseLabel = document.createElement("label");
    repurchaseLabel.htmlFor = repurchaseCheck.id;
    repurchaseLabel.className = "adminShopCheckLabel";
    repurchaseLabel.appendChild(repurchaseCheck);
    repurchaseLabel.appendChild(document.createTextNode(" Allow repurchase (re-tint)"));

    const limitedCheck = document.createElement("input");
    limitedCheck.type = "checkbox";
    limitedCheck.id = "shopPublishLimited";
    limitedCheck.checked = !!editingItem?.limitedItem;
    const limitedLabel = document.createElement("label");
    limitedLabel.htmlFor = limitedCheck.id;
    limitedLabel.className = "adminShopCheckLabel";
    limitedLabel.appendChild(limitedCheck);
    limitedLabel.appendChild(document.createTextNode(" Limited Item"));

    const limitedDurationInput = document.createElement("input");
    limitedDurationInput.type = "text";
    limitedDurationInput.className = "formInput";
    limitedDurationInput.placeholder = "e.g. 1d, 2d 15h, 45m";
    limitedDurationInput.value = editingItem?.limitedDurationText || "";
    const limitedDurationRow = document.createElement("div");
    limitedDurationRow.className = "formRow";
    const limitedDurationLab = document.createElement("span");
    limitedDurationLab.className = "formLabel";
    limitedDurationLab.textContent = "Available for";
    limitedDurationRow.appendChild(limitedDurationLab);
    limitedDurationRow.appendChild(limitedDurationInput);
    const syncLimitedFields = () => {
      limitedDurationRow.classList.toggle("hidden", !limitedCheck.checked);
    };
    limitedCheck.onchange = syncLimitedFields;

    const unlockSelect = document.createElement("select");
    unlockSelect.className = "formInput";
    [
      ["dots", "Price in Dots"],
      ["achievement", "Achievement unlock"],
      ["challenge", "Challenge unlock"],
      ["score", "Score in mode"],
    ].forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      unlockSelect.appendChild(o);
    });
    if (editingItem) unlockSelect.value = editingItem.unlockType;

    const priceInput = document.createElement("input");
    priceInput.type = "number";
    priceInput.min = "0";
    priceInput.className = "formInput";
    priceInput.placeholder = "Price in Dots";
    priceInput.value = String(editingItem?.priceDots ?? 100);

    const achievementSelect = document.createElement("select");
    achievementSelect.className = "formInput hidden";
    const challengeInput = document.createElement("input");
    challengeInput.className = "formInput hidden";
    challengeInput.placeholder = "Challenge id";
    if (editingItem?.unlockChallengeId) challengeInput.value = editingItem.unlockChallengeId;
    const modeSelect = document.createElement("select");
    modeSelect.className = "formInput adminShopModeListbox";
    modeSelect.size = 8;
    modeSelect.setAttribute("aria-label", "Mode");
    SCORE_UNLOCK_MODES.forEach(({ id, label }) => {
      const o = document.createElement("option");
      o.value = id;
      o.textContent = label;
      modeSelect.appendChild(o);
    });
    if (SCORE_UNLOCK_MODES.length) modeSelect.value = SCORE_UNLOCK_MODES[0].id;
    if (editingItem?.unlockScoreMode) modeSelect.value = editingItem.unlockScoreMode;
    const scoreInput = document.createElement("input");
    scoreInput.type = "number";
    scoreInput.min = "1";
    scoreInput.className = "formInput hidden";
    scoreInput.placeholder = "Required score";
    if (editingItem?.unlockScoreValue != null) scoreInput.value = String(editingItem.unlockScoreValue);

    const syncUnlockFields = () => {
      const t = unlockSelect.value;
      priceInput.classList.toggle("hidden", t !== "dots");
      achievementSelect.classList.toggle("hidden", t !== "achievement");
      challengeInput.classList.toggle("hidden", t !== "challenge");
      modeSelect.classList.toggle("hidden", t !== "score");
      scoreInput.classList.toggle("hidden", t !== "score");
    };
    unlockSelect.onchange = syncUnlockFields;

    try {
      const achievements = await fetchAdminAchievements();
      achievements.forEach((a) => {
        const o = document.createElement("option");
        o.value = a.id;
        o.textContent = a.title;
        achievementSelect.appendChild(o);
      });
      if (editingItem?.unlockAchievementId) achievementSelect.value = editingItem.unlockAchievementId;
    } catch {
      /* ignore */
    }

    const err = document.createElement("p");
    err.className = "accessHint";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = "btn green";
    okBtn.textContent = "Publish";

    const row = (label: string, el: HTMLElement, parent: HTMLElement = box) => {
      const fr = document.createElement("div");
      fr.className = "formRow";
      const sp = document.createElement("span");
      sp.className = "formLabel";
      sp.textContent = label;
      fr.appendChild(sp);
      fr.appendChild(el);
      parent.appendChild(fr);
      return fr;
    };

    row("Save as", destSelect);
    row("Replace", replaceOtherSelect);
    box.appendChild(sendPanel);
    row("Name", nameInput);
    const categoryRow = row("Category", categorySelect);
    box.appendChild(customColorLabel);
    box.appendChild(repurchaseLabel);
    shopFieldsWrap.appendChild(limitedLabel);
    shopFieldsWrap.appendChild(limitedDurationRow);
    row("Unlock", unlockSelect, shopFieldsWrap);
    row("Price", priceInput, shopFieldsWrap);
    row("Achievement", achievementSelect, shopFieldsWrap);
    row("Challenge id", challengeInput, shopFieldsWrap);
    row("Mode", modeSelect, shopFieldsWrap);
    row("Score", scoreInput, shopFieldsWrap);
    box.appendChild(shopFieldsWrap);
    box.appendChild(err);
    if (cosmeticKind === "trail" || cosmeticKind === "banner" || cosmeticKind === "theme") {
      categoryRow.classList.add("hidden");
      customColorLabel.classList.add("hidden");
      repurchaseLabel.classList.add("hidden");
    } else if (newCursorCheck.checked) {
      const lockHint = document.createElement("p");
      lockHint.className = "accessHint";
      lockHint.textContent = "New Cursor is on — category is locked to Cursor.";
      categoryRow.after(lockHint);
    }
    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    actions.appendChild(cancelBtn);
    actions.appendChild(okBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);
    syncUnlockFields();
    syncDestFields();
    syncLimitedFields();

    cancelBtn.onclick = () => modal.remove();
    okBtn.onclick = async () => {
      const name = nameInput.value.trim();
      if (!name) {
        err.textContent = "Name required.";
        return;
      }
      const dest = destSelect.value;
      if (dest === "send") {
        if (!recipients.length) {
          err.textContent = "Add at least one recipient.";
          return;
        }
        const art = buildTrailOrCursorArtFields(
          categorySelect,
          customColorCheck.checked,
          repurchaseCheck.checked,
        );
        const sendIds = recipients.map((r) => r.id);
        if (editingItem?.sentOnly) {
          if (!opts?.permissions?.overwrite) {
            err.textContent = "Missing Overwrite Shop Items permission.";
            return;
          }
          err.textContent = "Sending…";
          try {
            const res = await patchAdminShopCosmetic({
              id: editingItem.id,
              name,
              ...art,
              sentOnly: true,
              unlockType: "free",
              sendToUserIds: sendIds,
            });
            modal.remove();
            const n = res.sent?.recipients ?? sendIds.length;
            status.textContent = `"${name}" updated and sent to ${n} user(s).`;
            editingItem = null;
          } catch (e) {
            err.textContent = e instanceof Error ? e.message : "Send failed.";
          }
          return;
        }
        if (!opts?.permissions?.publish) {
          err.textContent = "Missing Shop Creator permission.";
          return;
        }
        err.textContent = "Sending…";
        try {
          const res = await publishAdminShopCosmetic({
            name,
            ...art,
            unlockType: "free",
            sentOnly: true,
            sendToUserIds: sendIds,
          });
          modal.remove();
          const n = res.sent?.recipients ?? sendIds.length;
          status.textContent = `"${name}" sent to ${n} user(s).`;
          editingItem = null;
        } catch (e) {
          err.textContent = e instanceof Error ? e.message : "Send failed.";
        }
        return;
      }
      if (limitedCheck.checked && !limitedDurationInput.value.trim()) {
        err.textContent = "Enter how long the Limited Item stays in Shop (e.g. 1d or 2d 15h).";
        return;
      }
      if (limitedCheck.checked && !parseLimitedDurationMs(limitedDurationInput.value)) {
        err.textContent = "Invalid duration. Use formats like 1d, 2d 15h, or 45m.";
        return;
      }
      let replaceId: string | null = null;
      if (dest === "replace-self" && editingItem) replaceId = editingItem.id;
      if (dest === "replace-other") {
        replaceId = replaceOtherSelect.value || null;
        if (!replaceId) {
          err.textContent = "Pick an item to replace.";
          return;
        }
      }
      if (replaceId) {
        if (!opts?.permissions?.overwrite) {
          err.textContent = "Missing Overwrite Shop Items permission.";
          return;
        }
      } else if (!opts?.permissions?.publish) {
        err.textContent = "Missing Shop Creator permission.";
        return;
      }
      err.textContent = replaceId ? "Replacing…" : "Publishing…";
      try {
        const payload = {
          name,
          ...buildTrailOrCursorArtFields(
            categorySelect,
            customColorCheck.checked,
            repurchaseCheck.checked,
          ),
          unlockType: unlockSelect.value as ShopUnlockType,
          priceDots: unlockSelect.value === "dots" ? Number(priceInput.value) : undefined,
          unlockAchievementId:
            unlockSelect.value === "achievement" ? achievementSelect.value : undefined,
          unlockChallengeId:
            unlockSelect.value === "challenge" ? challengeInput.value.trim() : undefined,
          unlockScoreMode: unlockSelect.value === "score" ? modeSelect.value : undefined,
          unlockScoreValue:
            unlockSelect.value === "score" ? Number(scoreInput.value) : undefined,
          limitedItem: limitedCheck.checked,
          limitedDurationText: limitedCheck.checked ? limitedDurationInput.value.trim() : null,
          sentOnly: false,
        };
        if (replaceId) {
          await patchAdminShopCosmetic({ id: replaceId, ...payload });
          modal.remove();
          status.textContent = `"${name}" updated in Shop.`;
        } else {
          await publishAdminShopCosmetic(payload);
          modal.remove();
          status.textContent = `"${name}" published to Shop.`;
        }
        editingItem = null;
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "Publish failed.";
      }
    };
  }

  pushHistory();
  syncAnimControls();
  syncMatchCursorUi();
  syncCosmeticKindUi();
  syncLayersUi();
  redrawEditor();

  return {
    loadPublishedItem,
    loadCrate: (item) => {
      editingCrateId = item.id;
      editingItem = null;
      status.textContent = `Editing crate art "${item.name}".`;
      animEnabled = false;
      animCheck.checked = false;
      matchCursorCheck.checked = false;
      matchShadeInput.value = "0";
      syncMatchCursorUi();
      syncAnimControls();
      loadDataUrl(item.imageData);
    },
  };
}

export function mountAdminCampaignItemCreator(
  root: HTMLElement,
  opts?: PixelCreatorOpts,
): AdminShopCreatorApi {
  return mountAdminPixelCreator(root, "campaignItem", opts);
}

export function mountAdminTaskItemCreator(
  root: HTMLElement,
  opts?: PixelCreatorOpts,
): AdminShopCreatorApi {
  return mountAdminPixelCreator(root, "taskItem", opts);
}

export function mountAdminMarathonItemCreator(
  root: HTMLElement,
  opts?: PixelCreatorOpts,
): AdminShopCreatorApi {
  return mountAdminPixelCreator(root, "marathonItem", opts);
}

export function mountAdminBattlePassItemCreator(
  root: HTMLElement,
  opts?: PixelCreatorOpts,
): AdminShopCreatorApi {
  return mountAdminPixelCreator(root, "battlePassItem", opts);
}

export function mountAdminShopCreator(
  root: HTMLElement,
  opts?: PixelCreatorOpts,
): AdminShopCreatorApi {
  return mountAdminPixelCreator(root, "cosmetic", opts);
}

export function mountAdminCrateCreator(
  root: HTMLElement,
  opts?: PixelCreatorOpts,
): AdminShopCreatorApi {
  return mountAdminPixelCreator(root, "crateItem", opts);
}

export function mountAdminCrateArtEditor(
  root: HTMLElement,
  opts?: PixelCreatorOpts,
): AdminCrateArtEditorApi {
  return mountAdminPixelCreator(root, "crateArt", opts);
}

function pickDraftModal(items: AdminShopDraftSummary[]): Promise<string | null> {
  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.className = "adminShopLoadModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>Load project</h2>`;
    const list = document.createElement("div");
    list.className = "adminShopDraftList";
    items.forEach((item) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn adminShopDraftPickBtn";
      btn.textContent = `${item.name || "Untitled"} — ${item.authorName || "?"} — ${new Date(item.updatedAt).toLocaleString()}`;
      btn.onclick = () => {
        modal.remove();
        resolve(item.id);
      };
      list.appendChild(btn);
    });
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn";
    cancel.textContent = "Cancel";
    cancel.onclick = () => {
      modal.remove();
      resolve(null);
    };
    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    actions.appendChild(cancel);
    box.appendChild(list);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);
  });
}
